import { EventEmitter, Injectable, Output } from '@angular/core';
import { NotifierService } from 'angular-notifier';
import { ApiRequestService } from 'src/app/services/api-request/api-request.service';

@Injectable({
  providedIn: 'root'
})
export class AdvancedFilterService {
  public isAdvancedFilterOpen: boolean = false;
  public advanceFilter: any[] = [];

  @Output() applyFilterEmitter: EventEmitter<any> = new EventEmitter<any>();

  constructor(private apiRequest: ApiRequestService,
    private notifier: NotifierService
  ) { }

  close() {
    this.isAdvancedFilterOpen = false;
  }

  openFilter(appliedAdvanceFilter?: any[]) {
    if (appliedAdvanceFilter && appliedAdvanceFilter.length) {
      this.advanceFilter = this.normalizeFilters(appliedAdvanceFilter);
    } else if (!this.advanceFilter.length) {
      // this.getAdvanceFilter();
      this.notifier.notify('warning', 'No filters available to apply.');
    }
    this.isAdvancedFilterOpen = true;
  }

  reset() {
    this.advanceFilter.forEach(group => {
      (group.filters || []).forEach((filter: any) => this.resetFilter(filter));
    });
    this.applyFilterEmitter.emit({});
    this.close();
  }

  /**
   * Collect selected values keyed by filter.key. Only includes non-empty values.
   */
  getPayload(): { [key: string]: any } {
    const payload: { [key: string]: any } = {};
    this.advanceFilter.forEach(group => {
      (group.filters || []).forEach((filter: any) => {
        const value = filter.selectedValue;
        if (this.hasValue(value)) {
          payload[filter.key] = this.serializeValue(filter, value);
        }
      });
    });
    return payload;
  }

  /**
   * Format the selected value for API transport. Date-range values are
   * serialized as local `YYYY-MM-DD` strings so the server does not receive
   * a UTC-shifted (one-day-earlier) date for users in positive timezones.
   */
  private serializeValue(filter: any, value: any): any {
    if (filter && filter.type === 'date-range' && value && typeof value === 'object') {
      return {
        from: this.toLocalDateString(value.from),
        to: this.toLocalDateString(value.to)
      };
    }
    return value;
  }

  /**
   * Convert a Date (or date-like value) to a local `YYYY-MM-DD` string
   * without timezone conversion. Returns empty string for empty input.
   */
  private toLocalDateString(value: any): string {
    if (value === null || value === undefined || value === '') { return ''; }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  applyFilter() {
    const payload = this.getPayload();
    this.applyFilterEmitter.emit(payload);
    // this.close();
  }

  /**
   * Validates every filter flagged with `required: true`.
   * Marks invalid filters with `invalid: true` and returns the outcome.
   */
  validate(): { valid: boolean; message: string } {
    let valid = true;
    let firstErrorMessage = '';
    this.advanceFilter.forEach(group => {
      (group.filters || []).forEach((filter: any) => {
        filter.invalid = false;
        filter.validationMessage = '';

        // Required check
        if (filter.required && !this.hasValue(filter.selectedValue)) {
          filter.invalid = true;
          filter.validationMessage = `${filter.name || 'This field'} is required`;
          if (!firstErrorMessage) { firstErrorMessage = 'Please fill all required filters before applying.'; }
          valid = false;
          return;
        }

        // Range validation (date-range / from-to)
        const rangeError = this.validateRange(filter);
        if (rangeError) {
          filter.invalid = true;
          filter.validationMessage = rangeError;
          if (!firstErrorMessage) { firstErrorMessage = rangeError; }
          valid = false;
        }
      });
    });
    return {
      valid,
      message: firstErrorMessage
    };
  }

  /**
   * Validates date-range / from-to filters based on `filter.validation` config.
   * Returns error message string if invalid, empty string otherwise.
   */
  private validateRange(filter: any): string {
    if (!filter) { return ''; }
    if (!filter.validation) { return ''; }
    if (filter.type !== 'date-range' && filter.type !== 'from-to') { return ''; }

    const value = filter.selectedValue;
    if (!value || typeof value !== 'object') { return ''; }

    const fromRaw = value.from;
    const toRaw = value.to;

    // Skip if either side is empty — nothing to compare
    if (!this.hasValue(fromRaw) || !this.hasValue(toRaw)) { return ''; }

    const defaultMessage = (filter.validation && filter.validation.message)
      ? filter.validation.message
      : (filter.type === 'date-range'
          ? 'To date should be same as or after from date'
          : 'To value should be greater than or equal to from value');

    if (filter.type === 'date-range') {
      const fromDate = fromRaw instanceof Date ? fromRaw : new Date(fromRaw);
      const toDate = toRaw instanceof Date ? toRaw : new Date(toRaw);
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) { return ''; }
      if (toDate.getTime() < fromDate.getTime()) { return defaultMessage; }
      return '';
    }

    // from-to numeric / generic comparable
    const fromNum = Number(fromRaw);
    const toNum = Number(toRaw);
    if (!isNaN(fromNum) && !isNaN(toNum)) {
      if (toNum < fromNum) { return defaultMessage; }
      return '';
    }

    // Fallback string compare
    if (String(toRaw) < String(fromRaw)) { return defaultMessage; }
    return '';
  }

  clearInvalid(filter: any) {
    if (filter) {
      filter.invalid = false;
      filter.validationMessage = '';
    }
  }

  /* -------------------- Filter normalization helpers -------------------- */

  private normalizeFilters(groups: any[]): any[] {
    return groups.map(group => {
      const normalized = { ...group };
      normalized.filters = (group.filters || []).map((filter: any) => {
        this.initSelectedValue(filter);
        return filter;
      });
      return normalized;
    });
  }

  private initSelectedValue(filter: any) {
    switch (filter.type) {
      case 'multi-select':
        filter.selectedValue = filter.selectedValue || [];
        break;
      case 'toggle':
        filter.selectedValue = filter.selectedValue === true;
        break;
      case 'date-range':
      case 'from-to':
        filter.selectedValue = filter.selectedValue || { from: '', to: '' };
        break;
      default:
        filter.selectedValue = filter.selectedValue || '';
    }
  }

  private resetFilter(filter: any) {
    switch (filter.type) {
      case 'multi-select':
        filter.selectedValue = [];
        filter.options = JSON.parse(JSON.stringify(filter.optionsBackup || []));
        break;
      case 'select':
        filter.selectedValue = '';
        filter.options = JSON.parse(JSON.stringify(filter.optionsBackup || []));
        break;
      case 'toggle':
        filter.selectedValue = false;
        break;
      case 'date-range':
      case 'from-to':
        filter.selectedValue = { from: '', to: '' };
        break;
      default:
        filter.selectedValue = '';
    }
    filter.invalid = false;
    filter.validationMessage = '';
  }

  /**
   * Re-runs range validation on the given filter. Use for live validation
   * of date-range / from-to filters on every value change.
   */
  onRangeChange(filter: any) {
    if (!filter) { return; }
    const rangeError = this.validateRange(filter);
    if (rangeError) {
      filter.invalid = true;
      filter.validationMessage = rangeError;
    } else {
      filter.invalid = false;
      filter.validationMessage = '';
    }
  }

  private hasValue(value: any): boolean {
    if (value === null || value === undefined || value === '') { return false; }
    if (value instanceof Date) { return !isNaN(value.getTime()); }
    if (Array.isArray(value)) { return value.length > 0; }
    if (typeof value === 'object') {
      return Object.values(value).some(v => v !== '' && v !== null && v !== undefined);
    }
    return true;
  }

  /* -------------------- Sample data -------------------- */

  getAdvanceFilter1() {
    let result = {
                  "status": 1,
                  "groups": [
                    {
                      "name": "",
                      "subtitle": "",
                      "key": "first_group",
                      "show": true,
                      "filters": [
                        {
                          "name": "Type",
                          "key": "type",
                          "options": [
                            {
                              "name": "Quant",
                              "value": "1"
                            },
                            {
                              "name": "Qual",
                              "value": "2"
                            }
                          ],
                          "placeholder": "Select Type",
                          "type": "select",
                          "dependent": [
                            "project",
                            "study",
                            "created_by"
                          ],
                          "on_change": [
                            {
                              "value": "2",
                              "key": "target_group",
                              "type": "group",
                              "action": {
                                "show": true
                              }
                            },
                            {
                              "value": "1",
                              "key": "target_group",
                              "type": "group",
                              "action": {
                                "show": false
                              }
                            }
                          ]
                        },
                        {
                          "name": "Project",
                          "key": "project",
                          "options": [
                            {
                              "name": "test quota",
                              "value": "5f189956-82f4-4d51-a0d0-477b2f0f2479"
                            },
                            {
                              "name": "testing request",
                              "value": "bb7ea1e0-831c-40e0-b708-e4e6c0f9f4bc"
                            },
                            {
                              "name": "AMA -Crownit 2",
                              "value": "fbdf13d2-383a-4da3-95de-7129494cc2bd"
                            },
                            {
                              "name": "Amazon yearly panel_2026-feb",
                              "value": "ce65ac71-c3cf-4e31-8ce2-1ff99c2847b8"
                            },
                            {
                              "name": "C-16976 || Pj. HUL_Test",
                              "value": "60d52153-327e-43be-9d11-bcd95ae892e8"
                            },
                            {
                              "name": "Copy qa_qaul_test_study",
                              "value": "8ab5f3ba-3ddb-438c-9d72-09f18e5e9469"
                            },
                            {
                              "name": "CV Demo",
                              "value": "f1f61095-7465-4596-aea9-3d0a460b1955"
                            },
                            {
                              "name": "Pj Demo Brand Qual",
                              "value": "55724edc-7d1f-4fee-a823-9fb1784244c1"
                            },
                            {
                              "name": "Demo Qual",
                              "value": "a19c8aa7-35cb-4d77-8354-53fc9c3ae388"
                            },
                            {
                              "name": "Demo - Request check",
                              "value": "478d8be3-67e8-4683-9fb0-581c3c7bb77b"
                            },
                            {
                              "name": "Demo preview",
                              "value": "c1c2f640-d9c1-4f6f-b385-5aaef33666a8"
                            },
                            {
                              "name": "Test_video_download",
                              "value": "a7391b9f-3282-4bde-b01e-5651952c4682"
                            },
                            {
                              "name": "EIN of Mighty_ concept",
                              "value": "51023f0d-d3df-4a47-a322-65396658b33a"
                            },
                            {
                              "name": "testing",
                              "value": "bbedfd51-35e2-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "LS Master",
                              "value": "951ea700-f3f1-465d-ae09-ea7b64fa888b"
                            },
                            {
                              "name": "LS-Respo",
                              "value": "f0f9537b-4aff-4693-958b-cd5c44625a8c"
                            },
                            {
                              "name": "Ls_human Pain Users",
                              "value": "d7b0ebba-e9ce-489d-943d-3a2203e6baa7"
                            },
                            {
                              "name": "LS_Human_Digestion",
                              "value": "8ebd7442-7e12-4ec7-9216-2d33a2889fe6"
                            },
                            {
                              "name": "LS_Power",
                              "value": "3ae84519-2e3b-4e4d-81ef-cf4298bd1c0a"
                            },
                            {
                              "name": "Mighty_ concept & Pricing_24-085445-01 - File Upload Module",
                              "value": "f334e1f1-44be-42f6-856a-1eddd1f27e3d"
                            },
                            {
                              "name": "QA_new_test_1",
                              "value": "fae9c1ca-19f4-4974-8fad-7259925d8055"
                            },
                            {
                              "name": "MUMBLE (C - 8397) - MODULE 2 (SHOPALONG) INITIATION",
                              "value": "917799bc-77e0-4c7a-9e33-601021f12564"
                            },
                            {
                              "name": "New Project Test-1",
                              "value": "8afed61e-e16b-451f-b2c2-65199c757ae0"
                            },
                            {
                              "name": "qa_testing_21",
                              "value": "22c0cfed-324b-4787-9011-f531d8d51327"
                            },
                            {
                              "name": "Crownit",
                              "value": "bbedf7a5-35e2-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "Pj. Kesariya- Indulekha Track- Wave 9 (July)",
                              "value": "20169698-2362-4d5d-8fec-479946108433"
                            },
                            {
                              "name": "PJ_Vault",
                              "value": "bc9c47d9-d09e-4c4a-aa27-0b6deb8d32a2"
                            },
                            {
                              "name": "Pr Label Junior",
                              "value": "236ac03c-243e-4d7a-bd34-ebec956a6036"
                            },
                            {
                              "name": "Project Antacids | Concept Evaluation",
                              "value": "83d943a9-1052-46ed-b3f1-5b17f11e7b8f"
                            },
                            {
                              "name": "QA-NewBuilderTesting",
                              "value": "25ed5a5c-9724-4294-a0f1-da2b1cca1743"
                            },
                            {
                              "name": "QA Gender-Name Mismatch",
                              "value": "599c556c-4544-40b2-9149-c807384d10f5"
                            },
                            {
                              "name": "QA-=Gen",
                              "value": "bc308cf9-861a-4b7d-8fa5-9d249ec272e2"
                            },
                            {
                              "name": "QA-Project A1",
                              "value": "bbefb688-35e2-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "QA-Image upload test",
                              "value": "ee91b3ba-97e5-484c-acca-458281b31567"
                            },
                            {
                              "name": "qa_project_new",
                              "value": "c986b915-e54a-4093-b3fe-593d8d586ff9"
                            },
                            {
                              "name": "Qa_newQualProject 2",
                              "value": "a0e08b9e-661b-4f5f-8cb0-cc4138d54661"
                            },
                            {
                              "name": "qa-quota_23",
                              "value": "87dec4b6-c2eb-4249-bd24-8c98e1e539d7"
                            },
                            {
                              "name": "QA-VI test",
                              "value": "d3af5048-2eb2-4f9a-b8c7-865a974550b4"
                            },
                            {
                              "name": "QA-video",
                              "value": "23e15cee-b1c3-46e1-b0aa-901765ef33cc"
                            },
                            {
                              "name": "QA_EAGLEEYE_RUMANA",
                              "value": "3f803a89-09b2-4141-8a36-6bd35468810d"
                            },
                            {
                              "name": "QA_version11",
                              "value": "cfdf7234-8d42-4ff6-a9da-0ea3e6ba81cd"
                            },
                            {
                              "name": "qa_himanshu",
                              "value": "9c3eb0ae-38e2-4b56-b010-37298e66a499"
                            },
                            {
                              "name": "Qa_Topline_In_2",
                              "value": "787bf331-57a8-43c3-8f56-b8b82a6d69ae"
                            },
                            {
                              "name": "qa_quant_testing",
                              "value": "b0e09f96-a354-4440-9082-bda674d488a2"
                            },
                            {
                              "name": "QUAL GENERIC DABUR",
                              "value": "2384f0b0-99b6-45b6-a3c7-a88448c9a09c"
                            },
                            {
                              "name": "Qual Test",
                              "value": "10f5a503-23a6-4e90-800f-4e4cfbec3602"
                            },
                            {
                              "name": "RQ Milkia IPSOS",
                              "value": "11b0c044-c6b8-41a0-90cc-6ef97c0856c4"
                            },
                            {
                              "name": "Sample Project",
                              "value": "d5784c05-7e3e-48a5-bd09-5c371e51ac21"
                            },
                            {
                              "name": "Sun Pharma Demo ",
                              "value": "e1c0b65d-9881-496f-89d4-670d1d3b4c49"
                            },
                            {
                              "name": "Tata Sons -Main || C-17046",
                              "value": "915a4e1a-a85b-4135-8c00-5e5dcc9f4322"
                            },
                            {
                              "name": "testing_A",
                              "value": "dce3fbc3-417a-4d27-b38c-1b1199613dce"
                            },
                            {
                              "name": "testing user Criteria",
                              "value": "7facb069-b23f-444e-ba1a-3b5922506105"
                            },
                            {
                              "name": "testing_Video_Indexer",
                              "value": "2c0c8104-bc18-445f-ac56-f1cb0eb4ff04"
                            }
                          ],
                          "placeholder": "Select Project",
                          "type": "multi-select",
                          "dependent": [
                            "study",
                            "created_by"
                          ]
                        },
                        {
                          "name": "Study",
                          "key": "study",
                          "options": [
                            {
                              "name": " qa sig testing",
                              "value": "9f32259d-7798-4fe5-911a-3cba6a687da2"
                            },
                            {
                              "name": " testing 25-11 multi upload 2",
                              "value": "bfab862e-ffb1-4db2-a02e-42c007e858bb"
                            },
                            {
                              "name": "08-07-2026",
                              "value": "778ea0a7-53da-42c5-b987-3d75b82562d1"
                            },
                            {
                              "name": "16302",
                              "value": "c305de99-4229-42a6-b19d-0f7fbcba32d2"
                            },
                            {
                              "name": "16324",
                              "value": "eb95668b-b8c1-4b15-8db4-41bad5f248fa"
                            },
                            {
                              "name": "16344",
                              "value": "89cc3c0a-363b-44a7-af65-36792cb1b819"
                            },
                            {
                              "name": "16369",
                              "value": "0e4facb3-ce0f-48d3-a873-5ec8a6791c1f"
                            },
                            {
                              "name": "16370",
                              "value": "fb1ac6a6-41b3-4268-8170-a187279cf11a"
                            },
                            {
                              "name": "16611",
                              "value": "0dfda443-4d8d-443b-87ed-1070cd9f9d76"
                            },
                            {
                              "name": "16628",
                              "value": "4aa8678b-272f-4c46-875b-64c859a3a1f4"
                            },
                            {
                              "name": "16685",
                              "value": "8fd110d0-3300-4323-a56a-010a698269a8"
                            },
                            {
                              "name": "16712",
                              "value": "075eb5aa-82fb-42ad-9814-47810573ffe5"
                            },
                            {
                              "name": "16713",
                              "value": "6a53f908-106e-41ed-af88-f255cf85367b"
                            },
                            {
                              "name": "16729",
                              "value": "d5927635-16bc-4bb7-8a5d-cbccbd20d1b5"
                            },
                            {
                              "name": "16730",
                              "value": "993602e5-9167-41ff-a775-2b3536b1978b"
                            },
                            {
                              "name": "16733",
                              "value": "4c59776a-d63f-4797-bc27-d2974141deee"
                            },
                            {
                              "name": "16766",
                              "value": "88ba412e-3513-49d2-88ce-55d5f94d5b5f"
                            },
                            {
                              "name": "16800",
                              "value": "ace9bb7c-4cc2-4a58-b736-35124c402f49"
                            },
                            {
                              "name": "16803",
                              "value": "e85d170a-98b0-4635-af24-d1a1bd2fe403"
                            },
                            {
                              "name": "16809",
                              "value": "b20e3f1c-652e-40b1-9025-26e5d64bb3fd"
                            },
                            {
                              "name": "16813",
                              "value": "20a3d0de-06dd-4ae3-9a31-a62bbe5650da"
                            },
                            {
                              "name": "16945",
                              "value": "f0d2a366-0d0e-456c-a181-ed9caae92b62"
                            },
                            {
                              "name": "16979",
                              "value": "79b6d0f8-64b4-4788-a0a0-21ef3e07e52f"
                            },
                            {
                              "name": "16995",
                              "value": "88bbc56f-b510-408c-8f1e-504ef417d314"
                            },
                            {
                              "name": "17052",
                              "value": "1ea00dec-6b0a-400c-a85f-48863e59008c"
                            },
                            {
                              "name": "17054",
                              "value": "f9a6c179-714a-49f3-afac-8d8d64a67c38"
                            },
                            {
                              "name": "17205",
                              "value": "643c8ba0-f246-4b11-83de-24660dd1037b"
                            },
                            {
                              "name": "17223",
                              "value": "0a924446-6b88-462b-a16b-24c963885d37"
                            },
                            {
                              "name": "17224",
                              "value": "2bb67d08-82e1-4574-9ea7-9d55ae67cc3f"
                            },
                            {
                              "name": "17225",
                              "value": "9b6c6964-b0d7-4bca-9154-2f6f727407c5"
                            },
                            {
                              "name": "17226",
                              "value": "e3343ea1-8865-45de-892e-e835292bea8d"
                            },
                            {
                              "name": "17238",
                              "value": "75fe2b2e-578a-40b8-a256-18fbb2c0ef45"
                            },
                            {
                              "name": "17239",
                              "value": "c8cb00eb-855b-41b1-aac2-90dac575d67b"
                            },
                            {
                              "name": "17244",
                              "value": "e4196cc0-c02e-437f-b811-12fe84086743"
                            },
                            {
                              "name": "17245",
                              "value": "3794be4b-d586-4473-b6f3-2c7a94415cfa"
                            },
                            {
                              "name": "17247",
                              "value": "b7b70b0b-f92b-4a34-b695-9a878a85c8cb"
                            },
                            {
                              "name": "17248",
                              "value": "26912759-a8e2-4194-806b-5d557d3d1adb"
                            },
                            {
                              "name": "17319",
                              "value": "e94c3e81-d510-4faa-af1a-73c637b1a873"
                            },
                            {
                              "name": "17320",
                              "value": "ce184302-a3b0-455f-aa97-620fa0a66708"
                            },
                            {
                              "name": "17327",
                              "value": "600f6508-66f5-451c-9d3c-388cae9dcff7"
                            },
                            {
                              "name": "17368",
                              "value": "691a13d9-ae16-405a-b430-54788a299dfb"
                            },
                            {
                              "name": "17376",
                              "value": "7830512d-55e5-4487-9555-9a5f0769bf88"
                            },
                            {
                              "name": "17379",
                              "value": "d71a1746-12c9-44ef-9d53-f9819a797f97"
                            },
                            {
                              "name": "17380",
                              "value": "a9eec743-e727-4a11-bc41-b2ca997c624f"
                            },
                            {
                              "name": "17382",
                              "value": "e9f9bf58-440f-45df-8101-bd87ac253646"
                            },
                            {
                              "name": "17413",
                              "value": "65b85222-757c-4551-9f27-24d101f63fe0"
                            },
                            {
                              "name": "17446",
                              "value": "4e0f56c4-f7eb-4898-a737-69b03b7e7d72"
                            },
                            {
                              "name": "17460",
                              "value": "f7d1d108-c25a-43b7-ba9d-605e95767412"
                            },
                            {
                              "name": "17566",
                              "value": "94ade75e-7629-4e6b-ae8d-14d7aa6ce9e3"
                            },
                            {
                              "name": "17601",
                              "value": "408b3dfe-05cc-4a6a-b7e5-cf58293438ee"
                            },
                            {
                              "name": "18064",
                              "value": "a7d25ee4-2525-4c4b-a0c8-c39bfe630a03"
                            },
                            {
                              "name": "18086",
                              "value": "db1a1f3b-c304-4b23-8b73-f3f9c2715e21"
                            },
                            {
                              "name": "18124",
                              "value": "4e95bdd7-4dce-4e63-ac44-c3db2a1ba259"
                            },
                            {
                              "name": "18263",
                              "value": "200800ad-9433-4510-93df-0afa8e7117c8"
                            },
                            {
                              "name": "18450",
                              "value": "2cb5a5bd-f721-4860-8814-b26f2cdb7cb5"
                            },
                            {
                              "name": "9003_sign_test",
                              "value": "a9a12b0b-ada3-491d-842b-fec675ea58f6"
                            },
                            {
                              "name": "Achha sa",
                              "value": "86b7585a-0bc4-407e-a3cb-0265a2ceb63c"
                            },
                            {
                              "name": "AMA-C-9160",
                              "value": "9c696f32-fbb1-4cfd-9693-76eb8705f657"
                            },
                            {
                              "name": "Amazon yearly panel_2026-feb",
                              "value": "b7eca31c-cd85-4e79-8471-f63f34e6e9f6"
                            },
                            {
                              "name": "Anand",
                              "value": "1217bea0-f408-4174-8045-76bbd7f10d9d"
                            },
                            {
                              "name": "April",
                              "value": "8f82a9b4-09b9-46fa-8805-7c7bfc8995d7"
                            },
                            {
                              "name": "April'26",
                              "value": "31e5a316-359a-4796-bbb4-6721eb7368e7"
                            },
                            {
                              "name": "August",
                              "value": "c7351998-95a4-41e8-a7ed-7567d5681d62"
                            },
                            {
                              "name": "August Toplines",
                              "value": "ccbf5a7d-2fbf-420e-a0a7-66ba975ca0fc"
                            },
                            {
                              "name": "C-16976 || Pj. HUL_Test",
                              "value": "3083c296-1a20-4223-bd97-8199ffba062e"
                            },
                            {
                              "name": "cc mail 8-01-25",
                              "value": "6446bec1-3c37-4fd0-a9fe-934c2a6b3634"
                            },
                            {
                              "name": "Copy qa_qaul_test_study",
                              "value": "f34e8f55-4ee6-4a2f-a7db-2654d7679a33"
                            },
                            {
                              "name": "Count Test",
                              "value": "6237d5d7-36ff-4c43-b2f7-b74004fe0fb8"
                            },
                            {
                              "name": "CTI_Cuppy_ID_Central Java",
                              "value": "9286e21e-ea05-4fd2-b200-48e16f6ad30d"
                            },
                            {
                              "name": "CTI_Cuppy_ID_West Java",
                              "value": "e2e427b9-23d4-490d-bedc-5ef3033211c4"
                            },
                            {
                              "name": "CV Demo",
                              "value": "96610123-c39e-4081-b673-d42af72b68e8"
                            },
                            {
                              "name": "Demo",
                              "value": "86383896-6351-475f-9e23-f7b983c78e64"
                            },
                            {
                              "name": "Demo Brand Qual",
                              "value": "422bf4fe-ecc6-4e56-b0c8-42ace639d10c"
                            },
                            {
                              "name": "DEMO MAY",
                              "value": "01d0049f-4175-43e8-a20f-4016e474d957"
                            },
                            {
                              "name": "Demo Qual",
                              "value": "a3dc1520-9bfa-415a-a24e-196de780f5d5"
                            },
                            {
                              "name": "Demo Request Check",
                              "value": "6ad15bc5-e0e6-491d-b7ec-5c4c89efdb5d"
                            },
                            {
                              "name": "Demo ST Preview",
                              "value": "9804249d-ea7d-47dd-8ec3-e114260e6a2b"
                            },
                            {
                              "name": "Digestive ",
                              "value": "56a09122-e0dd-4c47-bbab-9b8989302753"
                            },
                            {
                              "name": "download_all",
                              "value": "4b38ff53-afd7-4311-9b3b-85fc53a42afc"
                            },
                            {
                              "name": "EIN of Mighty_ concept",
                              "value": "084b1232-d029-40fe-bf6a-3bffc92c702d"
                            },
                            {
                              "name": "February",
                              "value": "4ed0b97c-df7f-4a42-a688-cf6c4d4f88b7"
                            },
                            {
                              "name": "February",
                              "value": "73bf6b31-7bf8-4cd2-b877-eb848ec24b90"
                            },
                            {
                              "name": "file uplad scan",
                              "value": "1a55e07b-ea15-458c-8b9b-450ea428a104"
                            },
                            {
                              "name": "Files Check Study",
                              "value": "3de5cac0-0d33-47d5-a067-dcb3689d13e4"
                            },
                            {
                              "name": "Heavy Files",
                              "value": "d1efb63d-3298-443b-a5c8-a0178e2e0b88"
                            },
                            {
                              "name": "IMAGE TO TEXT",
                              "value": "68b93448-1a40-4d54-9302-bf29714272f9"
                            },
                            {
                              "name": "Img to text ",
                              "value": "5b32861c-abaf-463a-967e-b35adc996b2d"
                            },
                            {
                              "name": "img_test",
                              "value": "43d6ee63-bc4c-4559-b342-420eaf8b5c40"
                            },
                            {
                              "name": "january",
                              "value": "3cdc813e-35c4-4a23-a3ab-17169d709178"
                            },
                            {
                              "name": "July",
                              "value": "ae4807b8-e700-440a-8e93-2440db195964"
                            },
                            {
                              "name": "July",
                              "value": "3fbd8eb2-8c29-42ff-9766-b67ed89ab111"
                            },
                            {
                              "name": "July Toplines",
                              "value": "757d861d-d9d6-4f6a-89f9-ef3501958d44"
                            },
                            {
                              "name": "June",
                              "value": "ade0579b-5ada-4161-954e-c52c754f1fe1"
                            },
                            {
                              "name": "June Toplines",
                              "value": "89a3eaee-ac8d-4bcf-83b6-5423a58f4f5a"
                            },
                            {
                              "name": "LS Master",
                              "value": "7d2e1a8e-d89d-40e0-8aac-311d2338bc86"
                            },
                            {
                              "name": "LS-Respo",
                              "value": "842d6572-f362-49d4-8748-b84163e021a4"
                            },
                            {
                              "name": "LS_Comfort",
                              "value": "2f7e3a6d-a9a2-4103-a0e1-cb688a79153f"
                            },
                            {
                              "name": "Ls_human Pain Users",
                              "value": "4203da21-b99d-4d6c-881a-f1e4248ccc54"
                            },
                            {
                              "name": "LS_Human_Digestion",
                              "value": "822b7a6f-6a3c-4b75-82e6-708a956d029f"
                            },
                            {
                              "name": "LS_Power",
                              "value": "8ec443c2-66f6-4e51-8f4e-fe9c621cc5f8"
                            },
                            {
                              "name": "Mar'26",
                              "value": "71809fd5-5787-49b6-b845-c0f3f37cb515"
                            },
                            {
                              "name": "March",
                              "value": "784b3411-2495-4087-ad45-c440f3257967"
                            },
                            {
                              "name": "March",
                              "value": "06b63a20-1719-4896-a5f0-3e438b7d05cb"
                            },
                            {
                              "name": "March Test Toplines",
                              "value": "6062fad4-1a42-4b37-b8f7-5e7b154e894d"
                            },
                            {
                              "name": "March topline copy",
                              "value": "3c2ee752-fe70-49aa-b846-3cca479a2dd5"
                            },
                            {
                              "name": "March Toplines",
                              "value": "83e8ffe7-5a05-4f5a-ad41-9bb43473420e"
                            },
                            {
                              "name": "May",
                              "value": "4f5a7fdd-695a-42e1-8451-3ee12c893fcd"
                            },
                            {
                              "name": "May 26",
                              "value": "85a62e1b-062b-46fd-a1c9-2172b1a1311e"
                            },
                            {
                              "name": "May Toplines",
                              "value": "345c7f70-7bcc-477e-a09b-394225bf86f8"
                            },
                            {
                              "name": "Mighty_ concept & Pricing_24-085445-01 - File Upload Module",
                              "value": "19ad2a02-bd85-4e67-b6cc-a62e21e9f3dc"
                            },
                            {
                              "name": "Miraj Test",
                              "value": "d6d6213f-b021-45fa-9d7c-38997f92e795"
                            },
                            {
                              "name": "MultiCut Study",
                              "value": "6301b492-4a55-4a64-a44b-f98bc423e909"
                            },
                            {
                              "name": "MultiLevel Study",
                              "value": "b14ec2c4-9e77-4158-b40e-f8c16ff24da0"
                            },
                            {
                              "name": "MUMBLE (C - 8397) - MODULE 2 (SHOPALONG) INITIATION",
                              "value": "f1ce02e7-6273-4b46-b68c-b5f949ab36bb"
                            },
                            {
                              "name": "My Test Study File",
                              "value": "4461ddd6-a8ee-4145-9055-5824321e7571"
                            },
                            {
                              "name": "MyPersonalStudy",
                              "value": "4a4e5e28-b068-423e-b226-49b118b20009"
                            },
                            {
                              "name": "MyStudyEagle",
                              "value": "6e39b8b1-9c8c-4be4-8d84-3ffc2d410b03"
                            },
                            {
                              "name": "MyStudyTest",
                              "value": "1dbbecc2-f1df-4f85-95e4-bb77ca3d5452"
                            },
                            {
                              "name": "New Topline",
                              "value": "1d889025-17a7-415a-8350-020aa751a02d"
                            },
                            {
                              "name": "Newtest1",
                              "value": "d9b5986b-c8a1-47c9-bb24-0a844bebbc4b"
                            },
                            {
                              "name": "new_test",
                              "value": "d0e063b1-c814-4308-89e2-fce497bd0064"
                            },
                            {
                              "name": "November Toplines",
                              "value": "3c64f861-ee9f-4e27-a062-c4461df32e25"
                            },
                            {
                              "name": "October Toplines",
                              "value": "c78c2d09-07ae-4d74-aeb9-4877b43fe8b7"
                            },
                            {
                              "name": "Offline Survey Image Parse",
                              "value": "05008c69-f6fd-4130-9726-757ee441eb4b"
                            },
                            {
                              "name": "Pain Relief",
                              "value": "2f599fca-8798-48f3-b775-bc4283486f59"
                            },
                            {
                              "name": "Pizza Hut - v1",
                              "value": "03638b0c-35e3-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "Pj. Kesariya- Indulekha Track- Wave 9 (July)",
                              "value": "74149252-ddcd-48e4-8231-f1a356644d4e"
                            },
                            {
                              "name": "PJ_Vault",
                              "value": "c4c5fce0-1ce9-4560-8c05-c0b6e918c15f"
                            },
                            {
                              "name": "Practice Topline",
                              "value": "1c994f3b-15f1-404f-a73a-7cb7bcbb3a30"
                            },
                            {
                              "name": "Pranav",
                              "value": "17057760-73a0-462e-9c75-868397fc3b7e"
                            },
                            {
                              "name": "Project Antacids | Concept Evaluation",
                              "value": "b3e81414-8fd7-4e15-917d-7f612765e511"
                            },
                            {
                              "name": "PT Study",
                              "value": "1107a0c3-daf7-47ff-aca0-29a4d6e88b99"
                            },
                            {
                              "name": "PUNEET FILE",
                              "value": "58365b52-16ae-4808-b776-61eb5d4d2ce7"
                            },
                            {
                              "name": "QA",
                              "value": "47ac75f3-5075-4b73-87f6-cefbce47ed6d"
                            },
                            {
                              "name": "qa ",
                              "value": "8e207dad-b154-4fc1-86ee-12265d73dab1"
                            },
                            {
                              "name": "QA Gender-Name Mismatch",
                              "value": "8bf5dca2-5e14-4e52-9c2e-64ecf69a99d2"
                            },
                            {
                              "name": "QA Merge",
                              "value": "7137be23-a910-4361-982c-e53258182f34"
                            },
                            {
                              "name": "QA-=Gen",
                              "value": "783f9f0d-90f9-4a3a-9414-3f3518510d15"
                            },
                            {
                              "name": "QA-Check1",
                              "value": "03d46043-d36f-4ec1-b4e6-95911d34afe8"
                            },
                            {
                              "name": "QA-Cosmetic",
                              "value": "5c9023d4-8ebc-42a8-a2e2-e571d63769d7"
                            },
                            {
                              "name": "QA-Image upload test",
                              "value": "648bcfda-0745-4eba-8d94-a68e55db53d4"
                            },
                            {
                              "name": "QA-M1",
                              "value": "218df71f-aa8a-445d-9665-095930ee8a10"
                            },
                            {
                              "name": "QA-Multi",
                              "value": "bbd4236f-a350-4471-b268-bed0db3d0dfd"
                            },
                            {
                              "name": "QA-New QUAL test",
                              "value": "5aa3104c-631d-4ca7-993c-8977cf17990f"
                            },
                            {
                              "name": "qa-quota_23",
                              "value": "e243dbfb-4f53-438f-b1e6-805ad74d3842"
                            },
                            {
                              "name": "QA-Request Check",
                              "value": "12be3124-3aa2-41db-9aed-18a1419c26b6"
                            },
                            {
                              "name": "QA-Topline1",
                              "value": "ec05105a-62e2-45a7-961f-595861423fc3"
                            },
                            {
                              "name": "QA-VI test",
                              "value": "33895e08-5e19-40d4-892c-7584bf7c3708"
                            },
                            {
                              "name": "QA-video",
                              "value": "9bd32e47-2e29-4fc6-8747-0a760f6d96ed"
                            },
                            {
                              "name": "qa_1",
                              "value": "7dd37972-a97e-4ff0-9080-bde265d61041"
                            },
                            {
                              "name": "qa_1",
                              "value": "6653f945-79b4-4d79-a5fe-119855a3aca8"
                            },
                            {
                              "name": "qa_92",
                              "value": "ff3cea5e-72a4-4236-970d-e153a6109216"
                            },
                            {
                              "name": "qa_93",
                              "value": "9dd02637-7d4f-4cd7-8873-ba329d259593"
                            },
                            {
                              "name": "qa_94",
                              "value": "e748c694-5d5f-47d6-8530-c3c0249c9f8a"
                            },
                            {
                              "name": "qa_95",
                              "value": "f88ab053-f498-476a-84d1-a6fa6679b28b"
                            },
                            {
                              "name": "qa_96",
                              "value": "dade1ef4-968e-4db9-8ab6-1cb2da3123cf"
                            },
                            {
                              "name": "qa_97",
                              "value": "f2d5b120-1c36-41a9-8c9e-84a88c7e8524"
                            },
                            {
                              "name": "QA_98",
                              "value": "30ffe78a-13bd-4caf-99b8-e2f5a1de26e7"
                            },
                            {
                              "name": "QA_99",
                              "value": "afacabd1-95c8-4838-9c28-54e8dedcfc43"
                            },
                            {
                              "name": "qa_himanshu",
                              "value": "5bed7bcd-0930-4d29-a1bc-18171899db56"
                            },
                            {
                              "name": "Qa_Ks_Test",
                              "value": "93b4f060-8e49-4c05-9937-fe8efdecdbbd"
                            },
                            {
                              "name": "Qa_Ks_Test4",
                              "value": "124690ac-3f4a-4e51-b759-99b0611df825"
                            },
                            {
                              "name": "qa_live",
                              "value": "12ce421a-1af7-48a1-a9f6-e137c58e19b4"
                            },
                            {
                              "name": "qa_newQuant",
                              "value": "910558ca-1a61-4060-b8ad-63e1bb11399a"
                            },
                            {
                              "name": "qa_quota_24",
                              "value": "1620dfeb-b4b5-498d-ab54-f1974c5bc998"
                            },
                            {
                              "name": "QA_Rumana",
                              "value": "8535f8ba-39bb-4858-b694-541b896c3643"
                            },
                            {
                              "name": "qa_test_repo_1",
                              "value": "e37edf76-a1af-4dd5-9df2-390d3c868c11"
                            },
                            {
                              "name": "QUAL GENERIC DABUR",
                              "value": "2d82c7fe-d564-4b6d-9ace-214f917589c2"
                            },
                            {
                              "name": "Qual Test Study 2024",
                              "value": "8434a569-9db5-4bc2-8047-aa85fbfde472"
                            },
                            {
                              "name": "Respiratory (Otrivin)",
                              "value": "682e830a-5e11-4db5-80c5-436d310d3387"
                            },
                            {
                              "name": "Rin Bar Lucknow Hindi",
                              "value": "7262d3ad-c1dc-4a65-af88-8f720b8fca2e"
                            },
                            {
                              "name": "RQ Milkia IPSOS",
                              "value": "e687616f-0fc6-4050-abb5-1f42146204c1"
                            },
                            {
                              "name": "Sample Study",
                              "value": "08c24858-9d06-4bd9-996f-1275acaa3d2b"
                            },
                            {
                              "name": "Santiy Study",
                              "value": "77a098a8-5bd8-4c2c-a224-aa5d7c97d28b"
                            },
                            {
                              "name": "September",
                              "value": "6560f926-025d-4d03-8be4-5d28c1016bb4"
                            },
                            {
                              "name": "SignificanceTestStudy",
                              "value": "3cf86f8d-c602-4ccb-859e-eefa057740c6"
                            },
                            {
                              "name": "study1",
                              "value": "9ab36a7c-d19a-4849-be08-6880009a84cf"
                            },
                            {
                              "name": "Study2024",
                              "value": "7997adc3-fab7-4a94-b99f-12ff30c7dfcb"
                            },
                            {
                              "name": "Sun Pharma demo",
                              "value": "a3487353-d26a-4cef-bb30-f058f7378fa3"
                            },
                            {
                              "name": "Tata Sons -Main || C-17046",
                              "value": "22f42155-3559-4007-8260-3191b48802cb"
                            },
                            {
                              "name": "test",
                              "value": "431d84a6-724b-4cbc-ae8c-678acbf37ee6"
                            },
                            {
                              "name": "test",
                              "value": "444b81d2-2bdd-45f1-b512-90dfbe1ca575"
                            },
                            {
                              "name": "test",
                              "value": "0e356786-19dc-4f47-95a4-2ec98479e068"
                            },
                            {
                              "name": "Test",
                              "value": "92fa4838-af68-439f-91e7-d3a8b069e58e"
                            },
                            {
                              "name": "test 17",
                              "value": "b940bf0b-c113-4d7a-993c-b7cf7be71d91"
                            },
                            {
                              "name": "test 2",
                              "value": "1af94b97-0547-4d0e-bfb6-144d6e9d3e60"
                            },
                            {
                              "name": "test 3",
                              "value": "23312208-d392-4bc7-a472-4417ba0e1cc4"
                            },
                            {
                              "name": "test 5",
                              "value": "e06536b2-8dad-4cdc-9011-4ad6a0042721"
                            },
                            {
                              "name": "test b",
                              "value": "51eaa60f-6ba0-4e85-9081-d08240764059"
                            },
                            {
                              "name": "test ci2 7-01",
                              "value": "01421178-34a2-484f-9444-be5b6dc158f3"
                            },
                            {
                              "name": "Test Create",
                              "value": "8391fb22-e5f1-454c-b93e-91e983d1f600"
                            },
                            {
                              "name": "Test Files Zip",
                              "value": "c0591121-a336-4435-b0c2-0475db349b86"
                            },
                            {
                              "name": "Test Share Study",
                              "value": "85fda9f5-efbd-481f-b5f9-f040dd8b8d07"
                            },
                            {
                              "name": "test spss",
                              "value": "e5199e34-69fb-4afc-9e72-637d62358fed"
                            },
                            {
                              "name": "Test Study",
                              "value": "3871dab7-3cc8-4c6e-87d6-afd97df0676d"
                            },
                            {
                              "name": "TEST Study",
                              "value": "78c45624-8fc6-40a1-a9ef-57e2b6ca68eb"
                            },
                            {
                              "name": "Test study Priyanka",
                              "value": "c5e1579e-f7b5-44ec-95d7-3b7f155ab3b8"
                            },
                            {
                              "name": "Test u",
                              "value": "45381b89-0970-4569-87d4-a7eaee007d48"
                            },
                            {
                              "name": "Test Zip Survey to text",
                              "value": "6bfc7e67-d074-40e1-a885-bf9872544e2b"
                            },
                            {
                              "name": "test123",
                              "value": "e181e86b-a734-4587-8502-0adbef1d5ffd"
                            },
                            {
                              "name": "test12345",
                              "value": "041de178-90f1-439f-9e4e-e22697ea3bc4"
                            },
                            {
                              "name": "test2",
                              "value": "0e6e453e-995d-41cf-abd6-65417dc4e689"
                            },
                            {
                              "name": "Test2",
                              "value": "5967aa6a-1169-4775-8687-1c2f6dd213c7"
                            },
                            {
                              "name": "test2",
                              "value": "d22d3093-424c-4e1a-a9d1-03a42061b919"
                            },
                            {
                              "name": "Test2",
                              "value": "a3b61724-e0f8-462e-98e1-edb8e1f476ea"
                            },
                            {
                              "name": "test2",
                              "value": "7a4fb2ba-92ea-4701-971f-8de0966ea15d"
                            },
                            {
                              "name": "test2",
                              "value": "f47f0e44-eba0-455b-800e-156099c10e22"
                            },
                            {
                              "name": "TestBug",
                              "value": "492ec3b9-b1e9-4c69-a40e-eafdbcc42474"
                            },
                            {
                              "name": "testing",
                              "value": "ced7829e-6f6b-41b2-98bf-14295b92708a"
                            },
                            {
                              "name": "testing 1",
                              "value": "0b8222f8-68ab-4b64-98c6-b7ad848f5b82"
                            },
                            {
                              "name": "testing 25-11 multi upload ",
                              "value": "127f4204-92e6-409a-98e4-bfe0e4f47af3"
                            },
                            {
                              "name": "testing cc mails 2",
                              "value": "816c0c6e-c9c6-428d-b209-9331b953a1f3"
                            },
                            {
                              "name": "testing cc mails 4 08-01-25",
                              "value": "2cc7f87d-6c5f-4442-af27-1cfd6730972e"
                            },
                            {
                              "name": "testing cc mails on ci",
                              "value": "dcd17108-e8cb-427a-a90a-386656c19653"
                            },
                            {
                              "name": "testing ci 6-1 ",
                              "value": "24e280c2-dd62-49eb-83f3-a808f523fa84"
                            },
                            {
                              "name": "testing ci add ons ",
                              "value": "1bc24157-a5b3-4e92-971f-e496e65b6de3"
                            },
                            {
                              "name": "testing ci add ons",
                              "value": "a03ff578-92e0-45b8-a23c-ae3548e4905c"
                            },
                            {
                              "name": "testing ci add ons 2",
                              "value": "d5d77e87-b6c0-4121-bd55-a3190c469e70"
                            },
                            {
                              "name": "testing feedback",
                              "value": "df389686-42ce-4efd-b4d5-140f896cb659"
                            },
                            {
                              "name": "testing image",
                              "value": "3f8e6d9e-8dbf-415b-9271-d51787b71876"
                            },
                            {
                              "name": "testing live",
                              "value": "34a8bde9-0fce-44d8-9c56-08ae5a48a0b8"
                            },
                            {
                              "name": "testing quota",
                              "value": "ea9d10b6-e607-4804-850c-5ea144dd558c"
                            },
                            {
                              "name": "testing study user criteria",
                              "value": "dcddb3b1-50c4-4c9b-bc15-43be0bc232e3"
                            },
                            {
                              "name": "Testing survey to text ",
                              "value": "80a8d5b4-9706-4bea-af91-1681ea7694a7"
                            },
                            {
                              "name": "Testing topline",
                              "value": "79d28d0c-0c2f-4912-8bc4-9f5ae0107138"
                            },
                            {
                              "name": "Testingn",
                              "value": "4d603247-99a5-49ac-beb5-e2c2cb5de903"
                            },
                            {
                              "name": "Testingn",
                              "value": "ba462912-8ca0-48ae-8b31-f1bb0febc037"
                            },
                            {
                              "name": "testing_Video_Indexer",
                              "value": "bdcc117d-7aa6-40b5-b9a1-b7254fbd8f8e"
                            },
                            {
                              "name": "testsharestudy",
                              "value": "e039dc35-b8e1-4d72-b5e5-67c13ec8d7a8"
                            },
                            {
                              "name": "test_delete 1",
                              "value": "0c5a8cfe-b20c-4d60-b7d9-dcfaff64302b"
                            },
                            {
                              "name": "Test_img",
                              "value": "824d2d3c-ff8e-46d8-a05c-8dd000747b41"
                            },
                            {
                              "name": "TG",
                              "value": "beb2d863-f284-464b-83c7-4a89746d6636"
                            },
                            {
                              "name": "Topline assets",
                              "value": "fb66ad1d-9e43-4eba-b897-a9187e4a4ede"
                            },
                            {
                              "name": "toplines",
                              "value": "406be08a-453f-4d7c-b35e-ea9bce1f145f"
                            },
                            {
                              "name": "Translation",
                              "value": "d65140c3-980c-4bb7-a126-4c4fbd2c33a3"
                            },
                            {
                              "name": "Translation_15687",
                              "value": "a404776e-279d-4f53-a933-c84036ad3de5"
                            },
                            {
                              "name": "Translation_9795",
                              "value": "33379bfe-1ec8-422a-8654-b921946df2a6"
                            },
                            {
                              "name": "trst_live",
                              "value": "444fb3e1-ab12-4155-a0ae-2c0d743a8c1e"
                            },
                            {
                              "name": "Vi Study Summary",
                              "value": "5f310054-2788-4e5d-9c11-692a9dadb36f"
                            },
                            {
                              "name": "Vi Video",
                              "value": "12445515-c57a-4c01-b317-d3bc85d78c38"
                            },
                            {
                              "name": "Vishal-test",
                              "value": "a0ff61cb-74f8-4a0e-a770-7f3509d84bd4"
                            },
                            {
                              "name": "wc",
                              "value": "39a6b028-c842-410c-a80c-32abd82a2cee"
                            }
                          ],
                          "placeholder": "Select Study",
                          "type": "multi-select",
                          "dependent": [
                            "created_by"
                          ]
                        },
                        {
                          "name": "Created By",
                          "key": "created_by",
                          "options": [
                            {
                              "name": "Pranav Prakash",
                              "value": "0f364b85-2c58-4317-9017-58766cb3b888"
                            },
                            {
                              "name": "Neelesh Bali",
                              "value": "df9e6e47-feb7-40e3-bb64-95edd9b7b6f5"
                            },
                            {
                              "name": "Priyanka Gakhar",
                              "value": "50272f90-2dfb-4541-90f2-1bc4988ff596"
                            },
                            {
                              "name": "Gaurav Sachar",
                              "value": "976f8ad7-52d2-4905-99a6-2cd400e565e6"
                            },
                            {
                              "name": "Anand Mishra",
                              "value": "65ebbf94-3acd-4c92-ba56-e74f444dd1f5"
                            },
                            {
                              "name": "Puneet Sikri",
                              "value": "a4fdf50e-974f-4876-bf71-b39eaee073e8"
                            },
                            {
                              "name": "Shikha Sahni",
                              "value": "8837262c-2b90-4574-a8fc-1758a13bc559"
                            },
                            {
                              "name": "Anish",
                              "value": "02593da2-32de-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "Jyoti Gupta",
                              "value": "a62221af-1c82-427d-b08c-751a4e5caecb"
                            },
                            {
                              "name": "Gurpreet Singh",
                              "value": "02596350-32de-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "Ambuj Singh",
                              "value": "0258ac9d-32de-11e9-8d02-42010aa0000a"
                            },
                            {
                              "name": "mohit.mehta@ipsos.com",
                              "value": "ab4f73f0-af47-4d64-8930-a8c1434e34c9"
                            },
                            {
                              "name": "Shreya Dastidar",
                              "value": "191f3bc7-4b6b-429d-bed9-cb856e1047fd"
                            },
                            {
                              "name": "Rumana Khan",
                              "value": "dde5f0f8-85cd-40a9-997f-6a0ae140e71b"
                            },
                            {
                              "name": "Faiz Ahmad",
                              "value": "228d604f-7f12-4b71-b38e-97a9541c8953"
                            },
                            {
                              "name": "Aljozy Bazer",
                              "value": "7bbce090-fec4-4591-982d-19964e61fa36"
                            },
                            {
                              "name": "Karnail Saini",
                              "value": "995510ae-7b2d-4721-bdbb-c516cb4a5488"
                            },
                            {
                              "name": "Neelesh Bali",
                              "value": "a6ab2d8e-8ca7-455a-bc79-2871944cb85b"
                            },
                            {
                              "name": "Neelesh Bali",
                              "value": "018b702c-9891-43a8-8139-3b29b10f1718"
                            },
                            {
                              "name": "testOrgShare",
                              "value": "107ea188-f96a-4dc0-baeb-455821f1df49"
                            },
                            {
                              "name": "Arjav Jain",
                              "value": "492501f6-4542-433f-9eaf-b22ee37d92f2"
                            },
                            {
                              "name": "Simran Singla",
                              "value": "3e58504f-6614-439b-b110-6574efa6e48f"
                            },
                            {
                              "name": "Himanshu Malik",
                              "value": "174885c9-06b1-4b2f-b8aa-77c549b3b525"
                            },
                            {
                              "name": "Vishal Rai",
                              "value": "27f057d2-c148-441b-b297-7e242d1de403"
                            }
                          ],
                          "placeholder": "Select User",
                          "type": "multi-select",
                          "dependent": [
                            "study",
                            "project"
                          ]
                        },
                        {
                          "name": "File Type",
                          "key": "file_type",
                          "options": [
                            {
                              "name": "Image",
                              "value": "image"
                            },
                            {
                              "name": "Report",
                              "value": "report"
                            },
                            {
                              "name": "Questionnaire",
                              "value": "questionnaire"
                            },
                            {
                              "name": "DataTable PCT",
                              "value": "datatable-pct"
                            },
                            {
                              "name": "DataTable ABS",
                              "value": "datatable-abs"
                            },
                            {
                              "name": "Report KPIView",
                              "value": "report-kpiview"
                            },
                            {
                              "name": "Video",
                              "value": "video"
                            },
                            {
                              "name": "Audio",
                              "value": "audio"
                            },
                            {
                              "name": "Zip",
                              "value": "zip"
                            },
                            {
                              "name": "Translation",
                              "value": "translation"
                            }
                          ],
                          "placeholder": "Select File Type",
                          "type": "multi-select"
                        },
                        {
                          "name": "Uploaded at",
                          "key": "uploaded_at",
                          "options": {
                            "from": {
                              "from_date": "",
                              "placeholder": "From Date"
                            },
                            "to": {
                              "to_date": "",
                              "placeholder": "To Date"
                            }
                          },
                          "type": "date-range",
                          "validation": {
                            "rule": "dateRangeValidation",
                            "message": "To date should be same as or after from date",
                            "condition": "toDate >= fromDate"
                          }
                        }
                      ]
                    },
                    {
                      "name": "Target Group",
                      "key": "target_group",
                      "show": false,
                      "subtitle": "(For ICV Interviews)",
                      "filters": [
                        {
                          "name": "Gender",
                          "key": "gender",
                          "options": [
                            {
                              "name": "Male",
                              "value": "male"
                            },
                            {
                              "name": "Female",
                              "value": "female"
                            }
                          ],
                          "placeholder": "Select Gender",
                          "type": "multi-select"
                        },
                        {
                          "name": "Center",
                          "key": "center",
                          "options": [
                            {
                              "name": "Aalborg",
                              "value": "Aalborg"
                            },
                            {
                              "name": "Aarhus",
                              "value": "Aarhus"
                            },
                            {
                              "name": "Abbottabad",
                              "value": "Abbottabad"
                            },
                            {
                              "name": "Abohar",
                              "value": "Abohar"
                            },
                            {
                              "name": "Achalpur",
                              "value": "Achalpur"
                            },
                            {
                              "name": "Adampur",
                              "value": "Adampur"
                            },
                            {
                              "name": "Adelaide",
                              "value": "Adelaide"
                            },
                            {
                              "name": "Adilabad",
                              "value": "Adilabad"
                            },
                            {
                              "name": "Adoni",
                              "value": "Adoni"
                            },
                            {
                              "name": "Agartala",
                              "value": "Agartala"
                            },
                            {
                              "name": "Agra",
                              "value": "Agra"
                            },
                            {
                              "name": "Ahmedabad",
                              "value": "Ahmedabad"
                            },
                            {
                              "name": "Ahmednagar",
                              "value": "Ahmednagar"
                            },
                            {
                              "name": "Aizawl",
                              "value": "Aizawl"
                            },
                            {
                              "name": "Ajmer",
                              "value": "Ajmer"
                            },
                            {
                              "name": "Akbarpur",
                              "value": "Akbarpur"
                            },
                            {
                              "name": "Akola",
                              "value": "Akola"
                            },
                            {
                              "name": "Alappuzha",
                              "value": "Alappuzha"
                            },
                            {
                              "name": "Albury-Wodonga",
                              "value": "Albury-Wodonga"
                            },
                            {
                              "name": "Albury-Wodonga",
                              "value": "Albury-Wodonga"
                            },
                            {
                              "name": "Alibag",
                              "value": "Alibag"
                            },
                            {
                              "name": "Alice Springs",
                              "value": "Alice Springs"
                            },
                            {
                              "name": "Aligarh",
                              "value": "Aligarh"
                            },
                            {
                              "name": "Alwar",
                              "value": "Alwar"
                            },
                            {
                              "name": "Amahai",
                              "value": "Amahai"
                            },
                            {
                              "name": "Amaravati",
                              "value": "Amaravati"
                            },
                            {
                              "name": "Ambala",
                              "value": "Ambala"
                            },
                            {
                              "name": "Ambala Sadar",
                              "value": "Ambala Sadar"
                            },
                            {
                              "name": "Ambattur",
                              "value": "Ambattur"
                            },
                            {
                              "name": "Ambernath",
                              "value": "Ambernath"
                            },
                            {
                              "name": "Ambikapur",
                              "value": "Ambikapur"
                            },
                            {
                              "name": "Ambon",
                              "value": "Ambon"
                            },
                            {
                              "name": "Ambur",
                              "value": "Ambur"
                            },
                            {
                              "name": "Amnat Charoen",
                              "value": "Amnat Charoen"
                            },
                            {
                              "name": "Amravati",
                              "value": "Amravati"
                            },
                            {
                              "name": "Amreli",
                              "value": "Amreli"
                            },
                            {
                              "name": "Amritsar",
                              "value": "Amritsar"
                            },
                            {
                              "name": "Amroha",
                              "value": "Amroha"
                            },
                            {
                              "name": "Anand",
                              "value": "Anand"
                            },
                            {
                              "name": "Anantapur",
                              "value": "Anantapur"
                            },
                            {
                              "name": "Anantnag",
                              "value": "Anantnag"
                            },
                            {
                              "name": "Ang Thong",
                              "value": "Ang Thong"
                            },
                            {
                              "name": "Anthony Lagoon",
                              "value": "Anthony Lagoon"
                            },
                            {
                              "name": "Anuradhapura",
                              "value": "Anuradhapura"
                            },
                            {
                              "name": "Aranyaprathet",
                              "value": "Aranyaprathet"
                            },
                            {
                              "name": "Ararat",
                              "value": "Ararat"
                            },
                            {
                              "name": "Armidale",
                              "value": "Armidale"
                            },
                            {
                              "name": "Arrah",
                              "value": "Arrah"
                            },
                            {
                              "name": "Asansol",
                              "value": "Asansol"
                            },
                            {
                              "name": "Ashokenagar Kalyangarh",
                              "value": "Ashokenagar Kalyangarh"
                            },
                            {
                              "name": "Aurangabad",
                              "value": "Aurangabad"
                            },
                            {
                              "name": "Aurangabad",
                              "value": "Aurangabad"
                            },
                            {
                              "name": "Avadi",
                              "value": "Avadi"
                            },
                            {
                              "name": "Ayr",
                              "value": "Ayr"
                            },
                            {
                              "name": "Ayutthaya",
                              "value": "Ayutthaya"
                            },
                            {
                              "name": "Azamgarh",
                              "value": "Azamgarh"
                            },
                            {
                              "name": "Bacchus Marsh",
                              "value": "Bacchus Marsh"
                            },
                            {
                              "name": "Badaun",
                              "value": "Badaun"
                            },
                            {
                              "name": "Badulla",
                              "value": "Badulla"
                            },
                            {
                              "name": "Bagaha",
                              "value": "Bagaha"
                            },
                            {
                              "name": "Bagalkot",
                              "value": "Bagalkot"
                            },
                            {
                              "name": "Bagdogra",
                              "value": "Bagdogra"
                            },
                            {
                              "name": "Baghpat",
                              "value": "Baghpat"
                            },
                            {
                              "name": "Baharampur",
                              "value": "Baharampur"
                            },
                            {
                              "name": "Bahawalpur",
                              "value": "Bahawalpur"
                            },
                            {
                              "name": "Bahraich",
                              "value": "Bahraich"
                            },
                            {
                              "name": "Baidyabati",
                              "value": "Baidyabati"
                            },
                            {
                              "name": "Bairnsdale",
                              "value": "Bairnsdale"
                            },
                            {
                              "name": "Baleshwar",
                              "value": "Baleshwar"
                            },
                            {
                              "name": "Balikpapan",
                              "value": "Balikpapan"
                            },
                            {
                              "name": "Ballarat",
                              "value": "Ballarat"
                            },
                            {
                              "name": "Ballia",
                              "value": "Ballia"
                            },
                            {
                              "name": "Ballina",
                              "value": "Ballina"
                            },
                            {
                              "name": "Bally",
                              "value": "Bally"
                            },
                            {
                              "name": "Bally Town",
                              "value": "Bally Town"
                            },
                            {
                              "name": "Balranald",
                              "value": "Balranald"
                            },
                            {
                              "name": "Balurghat",
                              "value": "Balurghat"
                            },
                            {
                              "name": "Banda",
                              "value": "Banda"
                            },
                            {
                              "name": "Banda Aceh",
                              "value": "Banda Aceh"
                            },
                            {
                              "name": "Bandar Lampung",
                              "value": "Bandar Lampung"
                            },
                            {
                              "name": "Bandjarmasin",
                              "value": "Bandjarmasin"
                            },
                            {
                              "name": "Bandung",
                              "value": "Bandung"
                            },
                            {
                              "name": "Bangkok",
                              "value": "Bangkok"
                            },
                            {
                              "name": "Bankura",
                              "value": "Bankura"
                            },
                            {
                              "name": "Bannu",
                              "value": "Bannu"
                            },
                            {
                              "name": "Bansberia",
                              "value": "Bansberia"
                            },
                            {
                              "name": "Banswara",
                              "value": "Banswara"
                            },
                            {
                              "name": "Banyuwangi",
                              "value": "Banyuwangi"
                            },
                            {
                              "name": "Baran",
                              "value": "Baran"
                            },
                            {
                              "name": "Baranagar",
                              "value": "Baranagar"
                            },
                            {
                              "name": "Barasat",
                              "value": "Barasat"
                            },
                            {
                              "name": "Baraut",
                              "value": "Baraut"
                            },
                            {
                              "name": "Bardhaman",
                              "value": "Bardhaman"
                            },
                            {
                              "name": "Bareilly",
                              "value": "Bareilly"
                            },
                            {
                              "name": "Baripada",
                              "value": "Baripada"
                            },
                            {
                              "name": "Barnala",
                              "value": "Barnala"
                            },
                            {
                              "name": "Barpeta Road",
                              "value": "Barpeta Road"
                            },
                            {
                              "name": "Barpeta Town",
                              "value": "Barpeta Town"
                            },
                            {
                              "name": "Barrackpur",
                              "value": "Barrackpur"
                            },
                            {
                              "name": "Barshi",
                              "value": "Barshi"
                            },
                            {
                              "name": "Basirhat",
                              "value": "Basirhat"
                            },
                            {
                              "name": "Basti",
                              "value": "Basti"
                            },
                            {
                              "name": "Batemans Bay",
                              "value": "Batemans Bay"
                            },
                            {
                              "name": "Bathinda",
                              "value": "Bathinda"
                            },
                            {
                              "name": "Bathurst",
                              "value": "Bathurst"
                            },
                            {
                              "name": "Batticaloa",
                              "value": "Batticaloa"
                            },
                            {
                              "name": "Baubau",
                              "value": "Baubau"
                            },
                            {
                              "name": "Beaconsfield",
                              "value": "Beaconsfield"
                            },
                            {
                              "name": "Beaudesert",
                              "value": "Beaudesert"
                            },
                            {
                              "name": "Beawar",
                              "value": "Beawar"
                            },
                            {
                              "name": "Beechworth",
                              "value": "Beechworth"
                            },
                            {
                              "name": "Bega",
                              "value": "Bega"
                            },
                            {
                              "name": "Begusarai",
                              "value": "Begusarai"
                            },
                            {
                              "name": "Bekasi",
                              "value": "Bekasi"
                            },
                            {
                              "name": "Belagavi",
                              "value": "Belagavi"
                            },
                            {
                              "name": "Bell Bay",
                              "value": "Bell Bay"
                            },
                            {
                              "name": "Bellary",
                              "value": "Bellary"
                            },
                            {
                              "name": "Benalla",
                              "value": "Benalla"
                            },
                            {
                              "name": "Bendigo",
                              "value": "Bendigo"
                            },
                            {
                              "name": "Bengaluru",
                              "value": "Bengaluru"
                            },
                            {
                              "name": "Bengkulu",
                              "value": "Bengkulu"
                            },
                            {
                              "name": "Berhampur",
                              "value": "Berhampur"
                            },
                            {
                              "name": "Bettiah",
                              "value": "Bettiah"
                            },
                            {
                              "name": "Betul",
                              "value": "Betul"
                            },
                            {
                              "name": "Bhadrak",
                              "value": "Bhadrak"
                            },
                            {
                              "name": "Bhadravati",
                              "value": "Bhadravati"
                            },
                            {
                              "name": "Bhadreswar",
                              "value": "Bhadreswar"
                            },
                            {
                              "name": "Bhagalpur",
                              "value": "Bhagalpur"
                            },
                            {
                              "name": "Bharatpur",
                              "value": "Bharatpur"
                            },
                            {
                              "name": "Bhatpara",
                              "value": "Bhatpara"
                            },
                            {
                              "name": "Bhavnagar",
                              "value": "Bhavnagar"
                            },
                            {
                              "name": "Bhilai",
                              "value": "Bhilai"
                            },
                            {
                              "name": "Bhilwara",
                              "value": "Bhilwara"
                            },
                            {
                              "name": "Bhimavaram",
                              "value": "Bhimavaram"
                            },
                            {
                              "name": "Bhind",
                              "value": "Bhind"
                            },
                            {
                              "name": "Bhiwadi",
                              "value": "Bhiwadi"
                            },
                            {
                              "name": "Bhiwandi",
                              "value": "Bhiwandi"
                            },
                            {
                              "name": "Bhiwani",
                              "value": "Bhiwani"
                            },
                            {
                              "name": "Bhopal",
                              "value": "Bhopal"
                            },
                            {
                              "name": "Bhubaneswar",
                              "value": "Bhubaneswar"
                            },
                            {
                              "name": "Bhuj",
                              "value": "Bhuj"
                            },
                            {
                              "name": "Bhusawal",
                              "value": "Bhusawal"
                            },
                            {
                              "name": "Biak",
                              "value": "Biak"
                            },
                            {
                              "name": "Bid",
                              "value": "Bid"
                            },
                            {
                              "name": "Bidar",
                              "value": "Bidar"
                            },
                            {
                              "name": "Bidhan Nagar",
                              "value": "Bidhan Nagar"
                            },
                            {
                              "name": "Bihar Sharif",
                              "value": "Bihar Sharif"
                            },
                            {
                              "name": "Bijapur",
                              "value": "Bijapur"
                            },
                            {
                              "name": "Bijnor",
                              "value": "Bijnor"
                            },
                            {
                              "name": "Bikaner",
                              "value": "Bikaner"
                            },
                            {
                              "name": "Bilaspur",
                              "value": "Bilaspur"
                            },
                            {
                              "name": "Binjai",
                              "value": "Binjai"
                            },
                            {
                              "name": "Blackwater",
                              "value": "Blackwater"
                            },
                            {
                              "name": "Blitar",
                              "value": "Blitar"
                            },
                            {
                              "name": "Bogor",
                              "value": "Bogor"
                            },
                            {
                              "name": "Bokaro",
                              "value": "Bokaro"
                            },
                            {
                              "name": "Bongaigaon",
                              "value": "Bongaigaon"
                            },
                            {
                              "name": "Bongaon",
                              "value": "Bongaon"
                            },
                            {
                              "name": "Bontang",
                              "value": "Bontang"
                            },
                            {
                              "name": "Botad",
                              "value": "Botad"
                            },
                            {
                              "name": "Bourke",
                              "value": "Bourke"
                            },
                            {
                              "name": "Bowen",
                              "value": "Bowen"
                            },
                            {
                              "name": "Bowral",
                              "value": "Bowral"
                            },
                            {
                              "name": "Brahmapur",
                              "value": "Brahmapur"
                            },
                            {
                              "name": "Brisbane",
                              "value": "Brisbane"
                            },
                            {
                              "name": "Broken Hill",
                              "value": "Broken Hill"
                            },
                            {
                              "name": "Broome",
                              "value": "Broome"
                            },
                            {
                              "name": "Bua Yai",
                              "value": "Bua Yai"
                            },
                            {
                              "name": "Buderim",
                              "value": "Buderim"
                            },
                            {
                              "name": "Bueng Kan",
                              "value": "Bueng Kan"
                            },
                            {
                              "name": "Bukittinggi",
                              "value": "Bukittinggi"
                            },
                            {
                              "name": "Bulandshahr",
                              "value": "Bulandshahr"
                            },
                            {
                              "name": "Bunbury",
                              "value": "Bunbury"
                            },
                            {
                              "name": "Bundaberg",
                              "value": "Bundaberg"
                            },
                            {
                              "name": "Bundi",
                              "value": "Bundi"
                            },
                            {
                              "name": "Burdwan",
                              "value": "Burdwan"
                            },
                            {
                              "name": "Burhanpur",
                              "value": "Burhanpur"
                            },
                            {
                              "name": "Buriram",
                              "value": "Buriram"
                            },
                            {
                              "name": "Burnie",
                              "value": "Burnie"
                            },
                            {
                              "name": "Busselton",
                              "value": "Busselton"
                            },
                            {
                              "name": "Buxar",
                              "value": "Buxar"
                            },
                            {
                              "name": "Byron Bay",
                              "value": "Byron Bay"
                            },
                            {
                              "name": "Caboolture",
                              "value": "Caboolture"
                            },
                            {
                              "name": "Cairns",
                              "value": "Cairns"
                            },
                            {
                              "name": "Camden",
                              "value": "Camden"
                            },
                            {
                              "name": "Campbelltown",
                              "value": "Campbelltown"
                            },
                            {
                              "name": "Canberra",
                              "value": "Canberra"
                            },
                            {
                              "name": "Castlemaine",
                              "value": "Castlemaine"
                            },
                            {
                              "name": "Ceduna",
                              "value": "Ceduna"
                            },
                            {
                              "name": "Chachoengsao",
                              "value": "Chachoengsao"
                            },
                            {
                              "name": "Chainat",
                              "value": "Chainat"
                            },
                            {
                              "name": "Chaiyaphum",
                              "value": "Chaiyaphum"
                            },
                            {
                              "name": "Chaman",
                              "value": "Chaman"
                            },
                            {
                              "name": "Champdani",
                              "value": "Champdani"
                            },
                            {
                              "name": "Chandausi",
                              "value": "Chandausi"
                            },
                            {
                              "name": "Chandigarh",
                              "value": "Chandigarh"
                            },
                            {
                              "name": "Chandrapur",
                              "value": "Chandrapur"
                            },
                            {
                              "name": "Chanthaburi",
                              "value": "Chanthaburi"
                            },
                            {
                              "name": "Chapra",
                              "value": "Chapra"
                            },
                            {
                              "name": "Charaidep",
                              "value": "Charaidep"
                            },
                            {
                              "name": "Charleville",
                              "value": "Charleville"
                            },
                            {
                              "name": "Charters Towers",
                              "value": "Charters Towers"
                            },
                            {
                              "name": "Chas",
                              "value": "Chas"
                            },
                            {
                              "name": "Chennai",
                              "value": "Chennai"
                            },
                            {
                              "name": "Chhattarpur",
                              "value": "Chhattarpur"
                            },
                            {
                              "name": "Chhindwara",
                              "value": "Chhindwara"
                            },
                            {
                              "name": "Chiang Mai",
                              "value": "Chiang Mai"
                            },
                            {
                              "name": "Chiang Rai",
                              "value": "Chiang Rai"
                            },
                            {
                              "name": "Chikmagalur",
                              "value": "Chikmagalur"
                            },
                            {
                              "name": "Chilakaluripet",
                              "value": "Chilakaluripet"
                            },
                            {
                              "name": "Chiniot",
                              "value": "Chiniot"
                            },
                            {
                              "name": "Chitradurga",
                              "value": "Chitradurga"
                            },
                            {
                              "name": "Chittaurgarh",
                              "value": "Chittaurgarh"
                            },
                            {
                              "name": "Chittoor",
                              "value": "Chittoor"
                            },
                            {
                              "name": "Chon Buri",
                              "value": "Chon Buri"
                            },
                            {
                              "name": "Chumphon",
                              "value": "Chumphon"
                            },
                            {
                              "name": "Churu",
                              "value": "Churu"
                            },
                            {
                              "name": "Cilacap",
                              "value": "Cilacap"
                            },
                            {
                              "name": "Cirebon",
                              "value": "Cirebon"
                            },
                            {
                              "name": "Clare",
                              "value": "Clare"
                            },
                            {
                              "name": "Cobar",
                              "value": "Cobar"
                            },
                            {
                              "name": "Coffs Harbour",
                              "value": "Coffs Harbour"
                            },
                            {
                              "name": "Coimbatore",
                              "value": "Coimbatore"
                            },
                            {
                              "name": "Colac",
                              "value": "Colac"
                            },
                            {
                              "name": "Colombo",
                              "value": "Colombo"
                            },
                            {
                              "name": "Coober Pedy",
                              "value": "Coober Pedy"
                            },
                            {
                              "name": "Cooktown",
                              "value": "Cooktown"
                            },
                            {
                              "name": "Coolgardie",
                              "value": "Coolgardie"
                            },
                            {
                              "name": "Cooma",
                              "value": "Cooma"
                            },
                            {
                              "name": "Coonabarabran",
                              "value": "Coonabarabran"
                            },
                            {
                              "name": "Coonamble",
                              "value": "Coonamble"
                            },
                            {
                              "name": "Coorg",
                              "value": "Coorg"
                            },
                            {
                              "name": "Cootamundra",
                              "value": "Cootamundra"
                            },
                            {
                              "name": "Copenhagen",
                              "value": "Copenhagen"
                            },
                            {
                              "name": "Corowa",
                              "value": "Corowa"
                            },
                            {
                              "name": "Cowra",
                              "value": "Cowra"
                            },
                            {
                              "name": "Cuttack",
                              "value": "Cuttack"
                            },
                            {
                              "name": "Dabgram",
                              "value": "Dabgram"
                            },
                            {
                              "name": "Dalby",
                              "value": "Dalby"
                            },
                            {
                              "name": "Daman",
                              "value": "Daman"
                            },
                            {
                              "name": "Damoh",
                              "value": "Damoh"
                            },
                            {
                              "name": "Dampier",
                              "value": "Dampier"
                            },
                            {
                              "name": "Danapur",
                              "value": "Danapur"
                            },
                            {
                              "name": "Dankuni",
                              "value": "Dankuni"
                            },
                            {
                              "name": "Darbhanga",
                              "value": "Darbhanga"
                            },
                            {
                              "name": "Darjiling",
                              "value": "Darjiling"
                            },
                            {
                              "name": "Darwin",
                              "value": "Darwin"
                            },
                            {
                              "name": "Datia",
                              "value": "Datia"
                            },
                            {
                              "name": "Davanagere",
                              "value": "Davanagere"
                            },
                            {
                              "name": "Deception Bay",
                              "value": "Deception Bay"
                            },
                            {
                              "name": "Deesa",
                              "value": "Deesa"
                            },
                            {
                              "name": "Dehradun",
                              "value": "Dehradun"
                            },
                            {
                              "name": "Dehri",
                              "value": "Dehri"
                            },
                            {
                              "name": "Deniliquin",
                              "value": "Deniliquin"
                            },
                            {
                              "name": "Denpasar",
                              "value": "Denpasar"
                            },
                            {
                              "name": "Deoghar",
                              "value": "Deoghar"
                            },
                            {
                              "name": "Deoria",
                              "value": "Deoria"
                            },
                            {
                              "name": "Dera Ghazi Khan",
                              "value": "Dera Ghazi Khan"
                            },
                            {
                              "name": "Dera Ismail Khan",
                              "value": "Dera Ismail Khan"
                            },
                            {
                              "name": "Derby",
                              "value": "Derby"
                            },
                            {
                              "name": "Dergaon",
                              "value": "Dergaon"
                            },
                            {
                              "name": "Devonport",
                              "value": "Devonport"
                            },
                            {
                              "name": "Dewas",
                              "value": "Dewas"
                            },
                            {
                              "name": "Dhanbad",
                              "value": "Dhanbad"
                            },
                            {
                              "name": "Dharmavaram",
                              "value": "Dharmavaram"
                            },
                            {
                              "name": "Dhaulpur",
                              "value": "Dhaulpur"
                            },
                            {
                              "name": "Dhekiajuli",
                              "value": "Dhekiajuli"
                            },
                            {
                              "name": "Dhemaji",
                              "value": "Dhemaji"
                            },
                            {
                              "name": "Dhubri",
                              "value": "Dhubri"
                            },
                            {
                              "name": "Dhule",
                              "value": "Dhule"
                            },
                            {
                              "name": "Dibrugarh",
                              "value": "Dibrugarh"
                            },
                            {
                              "name": "Dimapur",
                              "value": "Dimapur"
                            },
                            {
                              "name": "Dindigul",
                              "value": "Dindigul"
                            },
                            {
                              "name": "Diphu",
                              "value": "Diphu"
                            },
                            {
                              "name": "Dispur",
                              "value": "Dispur"
                            },
                            {
                              "name": "Dombivli",
                              "value": "Dombivli"
                            },
                            {
                              "name": "Dubbo",
                              "value": "Dubbo"
                            },
                            {
                              "name": "Dum Dum",
                              "value": "Dum Dum"
                            },
                            {
                              "name": "Durg",
                              "value": "Durg"
                            },
                            {
                              "name": "Durgapur",
                              "value": "Durgapur"
                            },
                            {
                              "name": "East Godavari",
                              "value": "East Godavari"
                            },
                            {
                              "name": "Echuca",
                              "value": "Echuca"
                            },
                            {
                              "name": "Eluru",
                              "value": "Eluru"
                            },
                            {
                              "name": "Emerald",
                              "value": "Emerald"
                            },
                            {
                              "name": "Ende",
                              "value": "Ende"
                            },
                            {
                              "name": "English Bazar",
                              "value": "English Bazar"
                            },
                            {
                              "name": "Erode",
                              "value": "Erode"
                            },
                            {
                              "name": "Esbjerg",
                              "value": "Esbjerg"
                            },
                            {
                              "name": "Etah",
                              "value": "Etah"
                            },
                            {
                              "name": "Etawah",
                              "value": "Etawah"
                            },
                            {
                              "name": "Faisalabad",
                              "value": "Faisalabad"
                            },
                            {
                              "name": "Faridabad",
                              "value": "Faridabad"
                            },
                            {
                              "name": "Farrukhabad",
                              "value": "Farrukhabad"
                            },
                            {
                              "name": "Fatehpur",
                              "value": "Fatehpur"
                            },
                            {
                              "name": "Firozabad",
                              "value": "Firozabad"
                            },
                            {
                              "name": "Firozpur",
                              "value": "Firozpur"
                            },
                            {
                              "name": "Forbes",
                              "value": "Forbes"
                            },
                            {
                              "name": "Forster",
                              "value": "Forster"
                            },
                            {
                              "name": "Frederikshavn",
                              "value": "Frederikshavn"
                            },
                            {
                              "name": "Fremantle",
                              "value": "Fremantle"
                            },
                            {
                              "name": "Galle",
                              "value": "Galle"
                            },
                            {
                              "name": "Gandhidham",
                              "value": "Gandhidham"
                            },
                            {
                              "name": "Gandhinagar",
                              "value": "Gandhinagar"
                            },
                            {
                              "name": "Gangapur",
                              "value": "Gangapur"
                            },
                            {
                              "name": "Gangawati",
                              "value": "Gangawati"
                            },
                            {
                              "name": "Gangtok",
                              "value": "Gangtok"
                            },
                            {
                              "name": "Gatton",
                              "value": "Gatton"
                            },
                            {
                              "name": "Gautam Buddha Nagar",
                              "value": "Gautam Buddha Nagar"
                            },
                            {
                              "name": "Gawler",
                              "value": "Gawler"
                            },
                            {
                              "name": "Gaya",
                              "value": "Gaya"
                            },
                            {
                              "name": "Geelong",
                              "value": "Geelong"
                            },
                            {
                              "name": "Geraldton",
                              "value": "Geraldton"
                            },
                            {
                              "name": "Ghaziabad",
                              "value": "Ghaziabad"
                            },
                            {
                              "name": "Ghazipur",
                              "value": "Ghazipur"
                            },
                            {
                              "name": "Gilgit",
                              "value": "Gilgit"
                            },
                            {
                              "name": "Giridih",
                              "value": "Giridih"
                            },
                            {
                              "name": "Gladstone",
                              "value": "Gladstone"
                            },
                            {
                              "name": "Glen Innes",
                              "value": "Glen Innes"
                            },
                            {
                              "name": "Goa",
                              "value": "Goa"
                            },
                            {
                              "name": "Goalpara",
                              "value": "Goalpara"
                            },
                            {
                              "name": "Godhra",
                              "value": "Godhra"
                            },
                            {
                              "name": "Golaghat",
                              "value": "Golaghat"
                            },
                            {
                              "name": "Gold Coast",
                              "value": "Gold Coast"
                            },
                            {
                              "name": "Gonda",
                              "value": "Gonda"
                            },
                            {
                              "name": "Gondal",
                              "value": "Gondal"
                            },
                            {
                              "name": "Gondiya",
                              "value": "Gondiya"
                            },
                            {
                              "name": "Goolwa",
                              "value": "Goolwa"
                            },
                            {
                              "name": "Goondiwindi",
                              "value": "Goondiwindi"
                            },
                            {
                              "name": "Gopalpur",
                              "value": "Gopalpur"
                            },
                            {
                              "name": "Gorakhpur",
                              "value": "Gorakhpur"
                            },
                            {
                              "name": "Gorontalo",
                              "value": "Gorontalo"
                            },
                            {
                              "name": "Gosford",
                              "value": "Gosford"
                            },
                            {
                              "name": "Goulburn",
                              "value": "Goulburn"
                            },
                            {
                              "name": "Grafton",
                              "value": "Grafton"
                            },
                            {
                              "name": "Greater Noida",
                              "value": "Greater Noida"
                            },
                            {
                              "name": "Griffith",
                              "value": "Griffith"
                            },
                            {
                              "name": "Gudivada",
                              "value": "Gudivada"
                            },
                            {
                              "name": "Gujranwala",
                              "value": "Gujranwala"
                            },
                            {
                              "name": "Gujrat",
                              "value": "Gujrat"
                            },
                            {
                              "name": "Gulbarga",
                              "value": "Gulbarga"
                            },
                            {
                              "name": "Guna",
                              "value": "Guna"
                            },
                            {
                              "name": "Gundagai",
                              "value": "Gundagai"
                            },
                            {
                              "name": "Gunnedah",
                              "value": "Gunnedah"
                            },
                            {
                              "name": "Guntakal",
                              "value": "Guntakal"
                            },
                            {
                              "name": "Guntur",
                              "value": "Guntur"
                            },
                            {
                              "name": "Gurdaspur",
                              "value": "Gurdaspur"
                            },
                            {
                              "name": "Gurugram",
                              "value": "Gurugram"
                            },
                            {
                              "name": "Guwahati",
                              "value": "Guwahati"
                            },
                            {
                              "name": "Gwadar",
                              "value": "Gwadar"
                            },
                            {
                              "name": "Gwalior",
                              "value": "Gwalior"
                            },
                            {
                              "name": "Gympie",
                              "value": "Gympie"
                            },
                            {
                              "name": "Habra",
                              "value": "Habra"
                            },
                            {
                              "name": "Haflong",
                              "value": "Haflong"
                            },
                            {
                              "name": "Hailakandi",
                              "value": "Hailakandi"
                            },
                            {
                              "name": "Hajipur",
                              "value": "Hajipur"
                            },
                            {
                              "name": "Haldia",
                              "value": "Haldia"
                            },
                            {
                              "name": "Halisahar",
                              "value": "Halisahar"
                            },
                            {
                              "name": "Hamilton",
                              "value": "Hamilton"
                            },
                            {
                              "name": "Hamirpur",
                              "value": "Hamirpur"
                            },
                            {
                              "name": "Hanumangarh",
                              "value": "Hanumangarh"
                            },
                            {
                              "name": "Hapur",
                              "value": "Hapur"
                            },
                            {
                              "name": "Hardoi",
                              "value": "Hardoi"
                            },
                            {
                              "name": "Haridwar",
                              "value": "Haridwar"
                            },
                            {
                              "name": "Hassan",
                              "value": "Hassan"
                            },
                            {
                              "name": "Hat Yai",
                              "value": "Hat Yai"
                            },
                            {
                              "name": "Hathras",
                              "value": "Hathras"
                            },
                            {
                              "name": "Hay",
                              "value": "Hay"
                            },
                            {
                              "name": "Hazaribag",
                              "value": "Hazaribag"
                            },
                            {
                              "name": "Healesville",
                              "value": "Healesville"
                            },
                            {
                              "name": "Hervey Bay",
                              "value": "Hervey Bay"
                            },
                            {
                              "name": "Hillerod",
                              "value": "Hillerod"
                            },
                            {
                              "name": "Hindaun",
                              "value": "Hindaun"
                            },
                            {
                              "name": "Hindupur",
                              "value": "Hindupur"
                            },
                            {
                              "name": "Hinganghat",
                              "value": "Hinganghat"
                            },
                            {
                              "name": "Hisar",
                              "value": "Hisar"
                            },
                            {
                              "name": "Hobart",
                              "value": "Hobart"
                            },
                            {
                              "name": "Hojai",
                              "value": "Hojai"
                            },
                            {
                              "name": "Hooghly",
                              "value": "Hooghly"
                            },
                            {
                              "name": "Horsham",
                              "value": "Horsham"
                            },
                            {
                              "name": "Hoshangabad",
                              "value": "Hoshangabad"
                            },
                            {
                              "name": "Hospet",
                              "value": "Hospet"
                            },
                            {
                              "name": "Hosur",
                              "value": "Hosur"
                            },
                            {
                              "name": "Howrah",
                              "value": "Howrah"
                            },
                            {
                              "name": "Hua Hin",
                              "value": "Hua Hin"
                            },
                            {
                              "name": "Hubli",
                              "value": "Hubli"
                            },
                            {
                              "name": "Hugli and Chinsurah",
                              "value": "Hugli and Chinsurah"
                            },
                            {
                              "name": "Hyderabad",
                              "value": "Hyderabad"
                            },
                            {
                              "name": "Ichalkaranji",
                              "value": "Ichalkaranji"
                            },
                            {
                              "name": "Imphal",
                              "value": "Imphal"
                            },
                            {
                              "name": "Indore",
                              "value": "Indore"
                            },
                            {
                              "name": "Indramayu",
                              "value": "Indramayu"
                            },
                            {
                              "name": "Ingham",
                              "value": "Ingham"
                            },
                            {
                              "name": "Innisfail",
                              "value": "Innisfail"
                            },
                            {
                              "name": "Inverell",
                              "value": "Inverell"
                            },
                            {
                              "name": "Iron Knob",
                              "value": "Iron Knob"
                            },
                            {
                              "name": "Islamabad",
                              "value": "Islamabad"
                            },
                            {
                              "name": "Itanagar",
                              "value": "Itanagar"
                            },
                            {
                              "name": "Jabalpur",
                              "value": "Jabalpur"
                            },
                            {
                              "name": "Jaffna",
                              "value": "Jaffna"
                            },
                            {
                              "name": "Jagadhri",
                              "value": "Jagadhri"
                            },
                            {
                              "name": "Jagdalpur",
                              "value": "Jagdalpur"
                            },
                            {
                              "name": "Jaipur",
                              "value": "Jaipur"
                            },
                            {
                              "name": "Jaisalmer",
                              "value": "Jaisalmer"
                            },
                            {
                              "name": "Jakarta",
                              "value": "Jakarta"
                            },
                            {
                              "name": "Jalandhar",
                              "value": "Jalandhar"
                            },
                            {
                              "name": "Jalgaon",
                              "value": "Jalgaon"
                            },
                            {
                              "name": "Jalna",
                              "value": "Jalna"
                            },
                            {
                              "name": "Jalpaiguri",
                              "value": "Jalpaiguri"
                            },
                            {
                              "name": "Jamalpur",
                              "value": "Jamalpur"
                            },
                            {
                              "name": "Jambi",
                              "value": "Jambi"
                            },
                            {
                              "name": "Jammu",
                              "value": "Jammu"
                            },
                            {
                              "name": "Jamnagar",
                              "value": "Jamnagar"
                            },
                            {
                              "name": "Jamshedpur",
                              "value": "Jamshedpur"
                            },
                            {
                              "name": "Jamuria",
                              "value": "Jamuria"
                            },
                            {
                              "name": "Jaunpur",
                              "value": "Jaunpur"
                            },
                            {
                              "name": "Jayapura",
                              "value": "Jayapura"
                            },
                            {
                              "name": "Jehanabad",
                              "value": "Jehanabad"
                            },
                            {
                              "name": "Jember",
                              "value": "Jember"
                            },
                            {
                              "name": "Jetpur Navagadh",
                              "value": "Jetpur Navagadh"
                            },
                            {
                              "name": "Jhang",
                              "value": "Jhang"
                            },
                            {
                              "name": "Jhansi",
                              "value": "Jhansi"
                            },
                            {
                              "name": "Jhunjhunun",
                              "value": "Jhunjhunun"
                            },
                            {
                              "name": "Jodhpur",
                              "value": "Jodhpur"
                            },
                            {
                              "name": "Jorhat",
                              "value": "Jorhat"
                            },
                            {
                              "name": "Junagadh",
                              "value": "Junagadh"
                            },
                            {
                              "name": "Junee",
                              "value": "Junee"
                            },
                            {
                              "name": "Kadapa",
                              "value": "Kadapa"
                            },
                            {
                              "name": "Kaithal",
                              "value": "Kaithal"
                            },
                            {
                              "name": "Kakinada",
                              "value": "Kakinada"
                            },
                            {
                              "name": "Kalasin",
                              "value": "Kalasin"
                            },
                            {
                              "name": "Kalgoorlie",
                              "value": "Kalgoorlie"
                            },
                            {
                              "name": "Kalol",
                              "value": "Kalol"
                            },
                            {
                              "name": "Kalyan",
                              "value": "Kalyan"
                            },
                            {
                              "name": "Kalyani",
                              "value": "Kalyani"
                            },
                            {
                              "name": "Kamarhati",
                              "value": "Kamarhati"
                            },
                            {
                              "name": "Kambalda",
                              "value": "Kambalda"
                            },
                            {
                              "name": "Kamphaeng Phet",
                              "value": "Kamphaeng Phet"
                            },
                            {
                              "name": "Kamrup",
                              "value": "Kamrup"
                            },
                            {
                              "name": "Kanchanaburi",
                              "value": "Kanchanaburi"
                            },
                            {
                              "name": "Kanchrapara",
                              "value": "Kanchrapara"
                            },
                            {
                              "name": "Kandy",
                              "value": "Kandy"
                            },
                            {
                              "name": "Kannur",
                              "value": "Kannur"
                            },
                            {
                              "name": "Kanpur",
                              "value": "Kanpur"
                            },
                            {
                              "name": "Kanpur Cantonment",
                              "value": "Kanpur Cantonment"
                            },
                            {
                              "name": "Kanyakumari",
                              "value": "Kanyakumari"
                            },
                            {
                              "name": "Kapurthala",
                              "value": "Kapurthala"
                            },
                            {
                              "name": "Karachi",
                              "value": "Karachi"
                            },
                            {
                              "name": "Karaikkudi",
                              "value": "Karaikkudi"
                            },
                            {
                              "name": "Karaikudi",
                              "value": "Karaikudi"
                            },
                            {
                              "name": "Karimganj",
                              "value": "Karimganj"
                            },
                            {
                              "name": "Karimnagar",
                              "value": "Karimnagar"
                            },
                            {
                              "name": "Karimpur",
                              "value": "Karimpur"
                            },
                            {
                              "name": "Karnal",
                              "value": "Karnal"
                            },
                            {
                              "name": "Karur",
                              "value": "Karur"
                            },
                            {
                              "name": "Kasaragod",
                              "value": "Kasaragod"
                            },
                            {
                              "name": "Kasganj",
                              "value": "Kasganj"
                            },
                            {
                              "name": "Kashipur",
                              "value": "Kashipur"
                            },
                            {
                              "name": "Kasur",
                              "value": "Kasur"
                            },
                            {
                              "name": "Katanning",
                              "value": "Katanning"
                            },
                            {
                              "name": "Katherine",
                              "value": "Katherine"
                            },
                            {
                              "name": "Katihar",
                              "value": "Katihar"
                            },
                            {
                              "name": "Katoomba",
                              "value": "Katoomba"
                            },
                            {
                              "name": "Katra",
                              "value": "Katra"
                            },
                            {
                              "name": "Kavali",
                              "value": "Kavali"
                            },
                            {
                              "name": "Kediri",
                              "value": "Kediri"
                            },
                            {
                              "name": "Kempsey",
                              "value": "Kempsey"
                            },
                            {
                              "name": "Kendari",
                              "value": "Kendari"
                            },
                            {
                              "name": "Kerang",
                              "value": "Kerang"
                            },
                            {
                              "name": "Khammam",
                              "value": "Khammam"
                            },
                            {
                              "name": "Khandwa",
                              "value": "Khandwa"
                            },
                            {
                              "name": "Khanna",
                              "value": "Khanna"
                            },
                            {
                              "name": "Kharagpur",
                              "value": "Kharagpur"
                            },
                            {
                              "name": "Khardaha",
                              "value": "Khardaha"
                            },
                            {
                              "name": "Khargone",
                              "value": "Khargone"
                            },
                            {
                              "name": "Khon Kaen",
                              "value": "Khon Kaen"
                            },
                            {
                              "name": "Khora",
                              "value": "Khora"
                            },
                            {
                              "name": "Khordha",
                              "value": "Khordha"
                            },
                            {
                              "name": "Khurja",
                              "value": "Khurja"
                            },
                            {
                              "name": "Kiama",
                              "value": "Kiama"
                            },
                            {
                              "name": "Kilinochchi",
                              "value": "Kilinochchi"
                            },
                            {
                              "name": "Kingaroy",
                              "value": "Kingaroy"
                            },
                            {
                              "name": "Kingston",
                              "value": "Kingston"
                            },
                            {
                              "name": "Kishanganj",
                              "value": "Kishanganj"
                            },
                            {
                              "name": "Kobenhavn",
                              "value": "Kobenhavn"
                            },
                            {
                              "name": "Kochi",
                              "value": "Kochi"
                            },
                            {
                              "name": "Kodaikanal",
                              "value": "Kodaikanal"
                            },
                            {
                              "name": "Kohat",
                              "value": "Kohat"
                            },
                            {
                              "name": "Kokrajhar",
                              "value": "Kokrajhar"
                            },
                            {
                              "name": "Kolar",
                              "value": "Kolar"
                            },
                            {
                              "name": "Kolhapur",
                              "value": "Kolhapur"
                            },
                            {
                              "name": "Kolkata",
                              "value": "Kolkata"
                            },
                            {
                              "name": "Kollam",
                              "value": "Kollam"
                            },
                            {
                              "name": "Korba",
                              "value": "Korba"
                            },
                            {
                              "name": "Kota",
                              "value": "Kota"
                            },
                            {
                              "name": "Kotabumi",
                              "value": "Kotabumi"
                            },
                            {
                              "name": "Kottayam",
                              "value": "Kottayam"
                            },
                            {
                              "name": "Kovalam",
                              "value": "Kovalam"
                            },
                            {
                              "name": "Kozhikode",
                              "value": "Kozhikode"
                            },
                            {
                              "name": "Krabi",
                              "value": "Krabi"
                            },
                            {
                              "name": "Krishna",
                              "value": "Krishna"
                            },
                            {
                              "name": "Krishnanagar",
                              "value": "Krishnanagar"
                            },
                            {
                              "name": "Kualakapuas",
                              "value": "Kualakapuas"
                            },
                            {
                              "name": "Kulti",
                              "value": "Kulti"
                            },
                            {
                              "name": "Kumbakonam",
                              "value": "Kumbakonam"
                            },
                            {
                              "name": "Kundian",
                              "value": "Kundian"
                            },
                            {
                              "name": "Kupang",
                              "value": "Kupang"
                            },
                            {
                              "name": "Kurichi",
                              "value": "Kurichi"
                            },
                            {
                              "name": "Kurnool",
                              "value": "Kurnool"
                            },
                            {
                              "name": "Kurri Kurri",
                              "value": "Kurri Kurri"
                            },
                            {
                              "name": "Kurukshetra",
                              "value": "Kurukshetra"
                            },
                            {
                              "name": "Kuta",
                              "value": "Kuta"
                            },
                            {
                              "name": "Kwinana",
                              "value": "Kwinana"
                            },
                            {
                              "name": "Kyabram",
                              "value": "Kyabram"
                            },
                            {
                              "name": "Kyneton",
                              "value": "Kyneton"
                            },
                            {
                              "name": "Lahat",
                              "value": "Lahat"
                            },
                            {
                              "name": "Lahore",
                              "value": "Lahore"
                            },
                            {
                              "name": "Lake Cargelligo",
                              "value": "Lake Cargelligo"
                            },
                            {
                              "name": "Lakes Entrance",
                              "value": "Lakes Entrance"
                            },
                            {
                              "name": "Lakhimpur",
                              "value": "Lakhimpur"
                            },
                            {
                              "name": "Lalitpur",
                              "value": "Lalitpur"
                            },
                            {
                              "name": "Lampang",
                              "value": "Lampang"
                            },
                            {
                              "name": "Lamphun",
                              "value": "Lamphun"
                            },
                            {
                              "name": "Langsa",
                              "value": "Langsa"
                            },
                            {
                              "name": "Lanka",
                              "value": "Lanka"
                            },
                            {
                              "name": "Larkana",
                              "value": "Larkana"
                            },
                            {
                              "name": "Latur",
                              "value": "Latur"
                            },
                            {
                              "name": "Launceston",
                              "value": "Launceston"
                            },
                            {
                              "name": "Leigh Creek",
                              "value": "Leigh Creek"
                            },
                            {
                              "name": "Lhokseumawe",
                              "value": "Lhokseumawe"
                            },
                            {
                              "name": "Lismore",
                              "value": "Lismore"
                            },
                            {
                              "name": "Lithgow",
                              "value": "Lithgow"
                            },
                            {
                              "name": "Loei",
                              "value": "Loei"
                            },
                            {
                              "name": "Lonavala",
                              "value": "Lonavala"
                            },
                            {
                              "name": "Loni",
                              "value": "Loni"
                            },
                            {
                              "name": "Lop Buri",
                              "value": "Lop Buri"
                            },
                            {
                              "name": "Loxton",
                              "value": "Loxton"
                            },
                            {
                              "name": "Lucknow",
                              "value": "Lucknow"
                            },
                            {
                              "name": "Ludhiana",
                              "value": "Ludhiana"
                            },
                            {
                              "name": "Luwuk",
                              "value": "Luwuk"
                            },
                            {
                              "name": "Machilipatnam",
                              "value": "Machilipatnam"
                            },
                            {
                              "name": "Mackay",
                              "value": "Mackay"
                            },
                            {
                              "name": "Madanapalle",
                              "value": "Madanapalle"
                            },
                            {
                              "name": "Madavaram",
                              "value": "Madavaram"
                            },
                            {
                              "name": "Madhyamgram",
                              "value": "Madhyamgram"
                            },
                            {
                              "name": "Madiun",
                              "value": "Madiun"
                            },
                            {
                              "name": "Madurai",
                              "value": "Madurai"
                            },
                            {
                              "name": "Mae Hong Son",
                              "value": "Mae Hong Son"
                            },
                            {
                              "name": "Mae Sot",
                              "value": "Mae Sot"
                            },
                            {
                              "name": "Magelang",
                              "value": "Magelang"
                            },
                            {
                              "name": "Maha Sarakham",
                              "value": "Maha Sarakham"
                            },
                            {
                              "name": "Mahabaleshwar",
                              "value": "Mahabaleshwar"
                            },
                            {
                              "name": "Mahesana",
                              "value": "Mahesana"
                            },
                            {
                              "name": "Maheshtala",
                              "value": "Maheshtala"
                            },
                            {
                              "name": "Mainpuri",
                              "value": "Mainpuri"
                            },
                            {
                              "name": "Maitland",
                              "value": "Maitland"
                            },
                            {
                              "name": "Majene",
                              "value": "Majene"
                            },
                            {
                              "name": "Makale",
                              "value": "Makale"
                            },
                            {
                              "name": "Malang",
                              "value": "Malang"
                            },
                            {
                              "name": "Malappuram",
                              "value": "Malappuram"
                            },
                            {
                              "name": "Malegaon",
                              "value": "Malegaon"
                            },
                            {
                              "name": "Malerkotla",
                              "value": "Malerkotla"
                            },
                            {
                              "name": "Mamuju",
                              "value": "Mamuju"
                            },
                            {
                              "name": "Manado",
                              "value": "Manado"
                            },
                            {
                              "name": "Manali",
                              "value": "Manali"
                            },
                            {
                              "name": "Mandoli",
                              "value": "Mandoli"
                            },
                            {
                              "name": "Mandsaur",
                              "value": "Mandsaur"
                            },
                            {
                              "name": "Mandurah",
                              "value": "Mandurah"
                            },
                            {
                              "name": "Mandya",
                              "value": "Mandya"
                            },
                            {
                              "name": "Mangaldai",
                              "value": "Mangaldai"
                            },
                            {
                              "name": "Mangalore",
                              "value": "Mangalore"
                            },
                            {
                              "name": "Mango",
                              "value": "Mango"
                            },
                            {
                              "name": "Manipal",
                              "value": "Manipal"
                            },
                            {
                              "name": "Manokwari",
                              "value": "Manokwari"
                            },
                            {
                              "name": "Mansehra",
                              "value": "Mansehra"
                            },
                            {
                              "name": "Mardan",
                              "value": "Mardan"
                            },
                            {
                              "name": "Mareeba",
                              "value": "Mareeba"
                            },
                            {
                              "name": "Maroochydore",
                              "value": "Maroochydore"
                            },
                            {
                              "name": "Martapura",
                              "value": "Martapura"
                            },
                            {
                              "name": "Maryborough",
                              "value": "Maryborough"
                            },
                            {
                              "name": "Maryborough",
                              "value": "Maryborough"
                            },
                            {
                              "name": "Matara",
                              "value": "Matara"
                            },
                            {
                              "name": "Mathura",
                              "value": "Mathura"
                            },
                            {
                              "name": "Mau",
                              "value": "Mau"
                            },
                            {
                              "name": "Maumere",
                              "value": "Maumere"
                            },
                            {
                              "name": "Medan",
                              "value": "Medan"
                            },
                            {
                              "name": "Medinapur",
                              "value": "Medinapur"
                            },
                            {
                              "name": "Meekatharra",
                              "value": "Meekatharra"
                            },
                            {
                              "name": "Meerut",
                              "value": "Meerut"
                            },
                            {
                              "name": "Melbourne",
                              "value": "Melbourne"
                            },
                            {
                              "name": "Merauke",
                              "value": "Merauke"
                            },
                            {
                              "name": "Mildura",
                              "value": "Mildura"
                            },
                            {
                              "name": "Millicent",
                              "value": "Millicent"
                            },
                            {
                              "name": "Mirput Khas",
                              "value": "Mirput Khas"
                            },
                            {
                              "name": "Miryalaguda",
                              "value": "Miryalaguda"
                            },
                            {
                              "name": "Mirzapur",
                              "value": "Mirzapur"
                            },
                            {
                              "name": "Modinagar",
                              "value": "Modinagar"
                            },
                            {
                              "name": "Moe",
                              "value": "Moe"
                            },
                            {
                              "name": "Moga",
                              "value": "Moga"
                            },
                            {
                              "name": "Mohali",
                              "value": "Mohali"
                            },
                            {
                              "name": "Mojokerto",
                              "value": "Mojokerto"
                            },
                            {
                              "name": "Moonie",
                              "value": "Moonie"
                            },
                            {
                              "name": "Moradabad",
                              "value": "Moradabad"
                            },
                            {
                              "name": "Moranbah",
                              "value": "Moranbah"
                            },
                            {
                              "name": "Moratuwa",
                              "value": "Moratuwa"
                            },
                            {
                              "name": "Moree",
                              "value": "Moree"
                            },
                            {
                              "name": "Morena",
                              "value": "Morena"
                            },
                            {
                              "name": "Morigaon",
                              "value": "Morigaon"
                            },
                            {
                              "name": "Moruya",
                              "value": "Moruya"
                            },
                            {
                              "name": "Morvi",
                              "value": "Morvi"
                            },
                            {
                              "name": "Morwell",
                              "value": "Morwell"
                            },
                            {
                              "name": "Motihari",
                              "value": "Motihari"
                            },
                            {
                              "name": "Mount Barker",
                              "value": "Mount Barker"
                            },
                            {
                              "name": "Mount Gambier",
                              "value": "Mount Gambier"
                            },
                            {
                              "name": "Mount Isa",
                              "value": "Mount Isa"
                            },
                            {
                              "name": "Mount Morgan",
                              "value": "Mount Morgan"
                            },
                            {
                              "name": "Moura",
                              "value": "Moura"
                            },
                            {
                              "name": "Mughalsarai",
                              "value": "Mughalsarai"
                            },
                            {
                              "name": "Mukdahan",
                              "value": "Mukdahan"
                            },
                            {
                              "name": "Muktsar",
                              "value": "Muktsar"
                            },
                            {
                              "name": "Multan",
                              "value": "Multan"
                            },
                            {
                              "name": "Mumbai",
                              "value": "Mumbai"
                            },
                            {
                              "name": "Munger",
                              "value": "Munger"
                            },
                            {
                              "name": "Munnar",
                              "value": "Munnar"
                            },
                            {
                              "name": "Murray Bridge",
                              "value": "Murray Bridge"
                            },
                            {
                              "name": "Murwara",
                              "value": "Murwara"
                            },
                            {
                              "name": "Murwillumbah",
                              "value": "Murwillumbah"
                            },
                            {
                              "name": "Mussoorie",
                              "value": "Mussoorie"
                            },
                            {
                              "name": "Mustafabad",
                              "value": "Mustafabad"
                            },
                            {
                              "name": "Muswellbrook",
                              "value": "Muswellbrook"
                            },
                            {
                              "name": "Muzaffarnagar",
                              "value": "Muzaffarnagar"
                            },
                            {
                              "name": "Muzaffarpur",
                              "value": "Muzaffarpur"
                            },
                            {
                              "name": "Mysore",
                              "value": "Mysore"
                            },
                            {
                              "name": "Nabadwip",
                              "value": "Nabadwip"
                            },
                            {
                              "name": "Nabire",
                              "value": "Nabire"
                            },
                            {
                              "name": "Nadiad",
                              "value": "Nadiad"
                            },
                            {
                              "name": "Nagaon",
                              "value": "Nagaon"
                            },
                            {
                              "name": "Nagapattinam",
                              "value": "Nagapattinam"
                            },
                            {
                              "name": "Nagarcoil",
                              "value": "Nagarcoil"
                            },
                            {
                              "name": "Nagaur",
                              "value": "Nagaur"
                            },
                            {
                              "name": "Nagda",
                              "value": "Nagda"
                            },
                            {
                              "name": "Nagpur",
                              "value": "Nagpur"
                            },
                            {
                              "name": "Naihati",
                              "value": "Naihati"
                            },
                            {
                              "name": "Nainital",
                              "value": "Nainital"
                            },
                            {
                              "name": "Najafgarh",
                              "value": "Najafgarh"
                            },
                            {
                              "name": "Nakhon Nayok",
                              "value": "Nakhon Nayok"
                            },
                            {
                              "name": "Nakhon Pathom",
                              "value": "Nakhon Pathom"
                            },
                            {
                              "name": "Nakhon Phanom",
                              "value": "Nakhon Phanom"
                            },
                            {
                              "name": "Nakhon Ratchasima",
                              "value": "Nakhon Ratchasima"
                            },
                            {
                              "name": "Nakhon Sawan",
                              "value": "Nakhon Sawan"
                            },
                            {
                              "name": "Nakhon Si Thammarat",
                              "value": "Nakhon Si Thammarat"
                            },
                            {
                              "name": "Nalbari",
                              "value": "Nalbari"
                            },
                            {
                              "name": "Nalgonda",
                              "value": "Nalgonda"
                            },
                            {
                              "name": "Namakkal",
                              "value": "Namakkal"
                            },
                            {
                              "name": "Nambucca Heads",
                              "value": "Nambucca Heads"
                            },
                            {
                              "name": "Nan",
                              "value": "Nan"
                            },
                            {
                              "name": "Nanded",
                              "value": "Nanded"
                            },
                            {
                              "name": "Nandurbar",
                              "value": "Nandurbar"
                            },
                            {
                              "name": "Nandyal",
                              "value": "Nandyal"
                            },
                            {
                              "name": "Naracoorte",
                              "value": "Naracoorte"
                            },
                            {
                              "name": "Narasaraopet",
                              "value": "Narasaraopet"
                            },
                            {
                              "name": "Narathiwat",
                              "value": "Narathiwat"
                            },
                            {
                              "name": "Narrabri",
                              "value": "Narrabri"
                            },
                            {
                              "name": "Narrandera",
                              "value": "Narrandera"
                            },
                            {
                              "name": "Narrogin",
                              "value": "Narrogin"
                            },
                            {
                              "name": "Nashik",
                              "value": "Nashik"
                            },
                            {
                              "name": "Navi Mumbai",
                              "value": "Navi Mumbai"
                            },
                            {
                              "name": "Nawabshah",
                              "value": "Nawabshah"
                            },
                            {
                              "name": "Neemuch",
                              "value": "Neemuch"
                            },
                            {
                              "name": "Nellore",
                              "value": "Nellore"
                            },
                            {
                              "name": "New Delhi",
                              "value": "New Delhi"
                            },
                            {
                              "name": "New Norfolk",
                              "value": "New Norfolk"
                            },
                            {
                              "name": "Newcastle",
                              "value": "Newcastle"
                            },
                            {
                              "name": "Newman",
                              "value": "Newman"
                            },
                            {
                              "name": "Neyveli",
                              "value": "Neyveli"
                            },
                            {
                              "name": "Nizamabad",
                              "value": "Nizamabad"
                            },
                            {
                              "name": "Noida",
                              "value": "Noida"
                            },
                            {
                              "name": "Nong Bua Lamphu",
                              "value": "Nong Bua Lamphu"
                            },
                            {
                              "name": "Nong Khai",
                              "value": "Nong Khai"
                            },
                            {
                              "name": "Nonthaburi",
                              "value": "Nonthaburi"
                            },
                            {
                              "name": "North 24 Parganas",
                              "value": "North 24 Parganas"
                            },
                            {
                              "name": "North Barrackpur",
                              "value": "North Barrackpur"
                            },
                            {
                              "name": "North Dumdum",
                              "value": "North Dumdum"
                            },
                            {
                              "name": "North Lakhimpur",
                              "value": "North Lakhimpur"
                            },
                            {
                              "name": "Northam",
                              "value": "Northam"
                            },
                            {
                              "name": "Nowra-Bomaderry",
                              "value": "Nowra-Bomaderry"
                            },
                            {
                              "name": "Odense",
                              "value": "Odense"
                            },
                            {
                              "name": "Okara",
                              "value": "Okara"
                            },
                            {
                              "name": "Ongole",
                              "value": "Ongole"
                            },
                            {
                              "name": "Oodnadatta",
                              "value": "Oodnadatta"
                            },
                            {
                              "name": "Ooty",
                              "value": "Ooty"
                            },
                            {
                              "name": "Orai",
                              "value": "Orai"
                            },
                            {
                              "name": "Orange",
                              "value": "Orange"
                            },
                            {
                              "name": "Osmanabad",
                              "value": "Osmanabad"
                            },
                            {
                              "name": "Ozhukarai",
                              "value": "Ozhukarai"
                            },
                            {
                              "name": "Padang",
                              "value": "Padang"
                            },
                            {
                              "name": "Padangpanjang",
                              "value": "Padangpanjang"
                            },
                            {
                              "name": "Padangsidempuan",
                              "value": "Padangsidempuan"
                            },
                            {
                              "name": "Pakalongan",
                              "value": "Pakalongan"
                            },
                            {
                              "name": "Palakkad",
                              "value": "Palakkad"
                            },
                            {
                              "name": "Palakollu",
                              "value": "Palakollu"
                            },
                            {
                              "name": "Palangkaraya",
                              "value": "Palangkaraya"
                            },
                            {
                              "name": "Palanpur",
                              "value": "Palanpur"
                            },
                            {
                              "name": "Palembang",
                              "value": "Palembang"
                            },
                            {
                              "name": "Palghar",
                              "value": "Palghar"
                            },
                            {
                              "name": "Pali",
                              "value": "Pali"
                            },
                            {
                              "name": "Pallavaram",
                              "value": "Pallavaram"
                            },
                            {
                              "name": "Palopo",
                              "value": "Palopo"
                            },
                            {
                              "name": "Palu",
                              "value": "Palu"
                            },
                            {
                              "name": "Palwal",
                              "value": "Palwal"
                            },
                            {
                              "name": "Panaji",
                              "value": "Panaji"
                            },
                            {
                              "name": "Panchkula",
                              "value": "Panchkula"
                            },
                            {
                              "name": "Pangkalpinang",
                              "value": "Pangkalpinang"
                            },
                            {
                              "name": "Panihati",
                              "value": "Panihati"
                            },
                            {
                              "name": "Panipat",
                              "value": "Panipat"
                            },
                            {
                              "name": "Parachinar",
                              "value": "Parachinar"
                            },
                            {
                              "name": "Parbhani",
                              "value": "Parbhani"
                            },
                            {
                              "name": "Parepare",
                              "value": "Parepare"
                            },
                            {
                              "name": "Parkes",
                              "value": "Parkes"
                            },
                            {
                              "name": "Parramatta",
                              "value": "Parramatta"
                            },
                            {
                              "name": "Pasuruan",
                              "value": "Pasuruan"
                            },
                            {
                              "name": "Patan",
                              "value": "Patan"
                            },
                            {
                              "name": "Pataya",
                              "value": "Pataya"
                            },
                            {
                              "name": "Pathankot",
                              "value": "Pathankot"
                            },
                            {
                              "name": "Pathum Thani",
                              "value": "Pathum Thani"
                            },
                            {
                              "name": "Pati",
                              "value": "Pati"
                            },
                            {
                              "name": "Patiala",
                              "value": "Patiala"
                            },
                            {
                              "name": "Patna",
                              "value": "Patna"
                            },
                            {
                              "name": "Pattani",
                              "value": "Pattani"
                            },
                            {
                              "name": "Pekanbaru",
                              "value": "Pekanbaru"
                            },
                            {
                              "name": "Pematangsiantar",
                              "value": "Pematangsiantar"
                            },
                            {
                              "name": "Penrith",
                              "value": "Penrith"
                            },
                            {
                              "name": "Perabumulih",
                              "value": "Perabumulih"
                            },
                            {
                              "name": "Peraiyur",
                              "value": "Peraiyur"
                            },
                            {
                              "name": "Perth",
                              "value": "Perth"
                            },
                            {
                              "name": "Peshawar",
                              "value": "Peshawar"
                            },
                            {
                              "name": "Phangnga",
                              "value": "Phangnga"
                            },
                            {
                              "name": "Phatthalung",
                              "value": "Phatthalung"
                            },
                            {
                              "name": "Phayao",
                              "value": "Phayao"
                            },
                            {
                              "name": "Phetchabun",
                              "value": "Phetchabun"
                            },
                            {
                              "name": "Phetchaburi",
                              "value": "Phetchaburi"
                            },
                            {
                              "name": "Phichit",
                              "value": "Phichit"
                            },
                            {
                              "name": "Phitsanulok",
                              "value": "Phitsanulok"
                            },
                            {
                              "name": "Phrae",
                              "value": "Phrae"
                            },
                            {
                              "name": "Phuket",
                              "value": "Phuket"
                            },
                            {
                              "name": "Pilibhit",
                              "value": "Pilibhit"
                            },
                            {
                              "name": "Pinrang",
                              "value": "Pinrang"
                            },
                            {
                              "name": "Pithampur",
                              "value": "Pithampur"
                            },
                            {
                              "name": "Pontianak",
                              "value": "Pontianak"
                            },
                            {
                              "name": "Porbandar",
                              "value": "Porbandar"
                            },
                            {
                              "name": "Port Adelaide Enfield",
                              "value": "Port Adelaide Enfield"
                            },
                            {
                              "name": "Port Augusta",
                              "value": "Port Augusta"
                            },
                            {
                              "name": "Port Blair",
                              "value": "Port Blair"
                            },
                            {
                              "name": "Port Fairy",
                              "value": "Port Fairy"
                            },
                            {
                              "name": "Port Hedland",
                              "value": "Port Hedland"
                            },
                            {
                              "name": "Port Lincoln",
                              "value": "Port Lincoln"
                            },
                            {
                              "name": "Port Macquarie",
                              "value": "Port Macquarie"
                            },
                            {
                              "name": "Port Pirie",
                              "value": "Port Pirie"
                            },
                            {
                              "name": "Portland",
                              "value": "Portland"
                            },
                            {
                              "name": "Poso",
                              "value": "Poso"
                            },
                            {
                              "name": "Prachin Buri",
                              "value": "Prachin Buri"
                            },
                            {
                              "name": "Prachuap Khiri Khan",
                              "value": "Prachuap Khiri Khan"
                            },
                            {
                              "name": "Praya",
                              "value": "Praya"
                            },
                            {
                              "name": "Prayagraj",
                              "value": "Prayagraj"
                            },
                            {
                              "name": "Probolinggo",
                              "value": "Probolinggo"
                            },
                            {
                              "name": "Proddatur",
                              "value": "Proddatur"
                            },
                            {
                              "name": "Puducherry",
                              "value": "Puducherry"
                            },
                            {
                              "name": "Pudukkottai",
                              "value": "Pudukkottai"
                            },
                            {
                              "name": "Pune",
                              "value": "Pune"
                            },
                            {
                              "name": "Puri",
                              "value": "Puri"
                            },
                            {
                              "name": "Purnia",
                              "value": "Purnia"
                            },
                            {
                              "name": "Puruliya",
                              "value": "Puruliya"
                            },
                            {
                              "name": "Pushkar",
                              "value": "Pushkar"
                            },
                            {
                              "name": "Puttalan",
                              "value": "Puttalan"
                            },
                            {
                              "name": "P_Hyderabad",
                              "value": "P_Hyderabad"
                            },
                            {
                              "name": "Queanbeyan",
                              "value": "Queanbeyan"
                            },
                            {
                              "name": "Queenstown",
                              "value": "Queenstown"
                            },
                            {
                              "name": "Quetta",
                              "value": "Quetta"
                            },
                            {
                              "name": "Raba",
                              "value": "Raba"
                            },
                            {
                              "name": "Rae Bareli",
                              "value": "Rae Bareli"
                            },
                            {
                              "name": "Rahim Yar Khan",
                              "value": "Rahim Yar Khan"
                            },
                            {
                              "name": "Raichur",
                              "value": "Raichur"
                            },
                            {
                              "name": "Raiganj",
                              "value": "Raiganj"
                            },
                            {
                              "name": "Raigarh",
                              "value": "Raigarh"
                            },
                            {
                              "name": "Raipur",
                              "value": "Raipur"
                            },
                            {
                              "name": "Rajahmundry",
                              "value": "Rajahmundry"
                            },
                            {
                              "name": "Rajapalayam",
                              "value": "Rajapalayam"
                            },
                            {
                              "name": "Rajkot",
                              "value": "Rajkot"
                            },
                            {
                              "name": "Rajpur Sonarpur",
                              "value": "Rajpur Sonarpur"
                            },
                            {
                              "name": "Rajpura",
                              "value": "Rajpura"
                            },
                            {
                              "name": "Ramagundam",
                              "value": "Ramagundam"
                            },
                            {
                              "name": "Rampur",
                              "value": "Rampur"
                            },
                            {
                              "name": "Ranchi",
                              "value": "Ranchi"
                            },
                            {
                              "name": "Ranga Reddy",
                              "value": "Ranga Reddy"
                            },
                            {
                              "name": "Rangia",
                              "value": "Rangia"
                            },
                            {
                              "name": "Ranibennur",
                              "value": "Ranibennur"
                            },
                            {
                              "name": "Raniganj",
                              "value": "Raniganj"
                            },
                            {
                              "name": "Ranong",
                              "value": "Ranong"
                            },
                            {
                              "name": "Ratchaburi",
                              "value": "Ratchaburi"
                            },
                            {
                              "name": "Ratlam",
                              "value": "Ratlam"
                            },
                            {
                              "name": "Ratnapura",
                              "value": "Ratnapura"
                            },
                            {
                              "name": "Raurkela Industrial Township",
                              "value": "Raurkela Industrial Township"
                            },
                            {
                              "name": "Rawalpindi",
                              "value": "Rawalpindi"
                            },
                            {
                              "name": "Raymond Terrace",
                              "value": "Raymond Terrace"
                            },
                            {
                              "name": "Rayong",
                              "value": "Rayong"
                            },
                            {
                              "name": "Redcliffe",
                              "value": "Redcliffe"
                            },
                            {
                              "name": "Renmark",
                              "value": "Renmark"
                            },
                            {
                              "name": "Rewa",
                              "value": "Rewa"
                            },
                            {
                              "name": "Rewari",
                              "value": "Rewari"
                            },
                            {
                              "name": "Richmond",
                              "value": "Richmond"
                            },
                            {
                              "name": "Richmond",
                              "value": "Richmond"
                            },
                            {
                              "name": "Rishikesh",
                              "value": "Rishikesh"
                            },
                            {
                              "name": "Rishra",
                              "value": "Rishra"
                            },
                            {
                              "name": "Robertson Pet",
                              "value": "Robertson Pet"
                            },
                            {
                              "name": "Rockhampton",
                              "value": "Rockhampton"
                            },
                            {
                              "name": "Rohtak",
                              "value": "Rohtak"
                            },
                            {
                              "name": "Roi Et",
                              "value": "Roi Et"
                            },
                            {
                              "name": "Roma",
                              "value": "Roma"
                            },
                            {
                              "name": "Roorkee",
                              "value": "Roorkee"
                            },
                            {
                              "name": "Rosebery",
                              "value": "Rosebery"
                            },
                            {
                              "name": "Roskilde",
                              "value": "Roskilde"
                            },
                            {
                              "name": "Rourkela",
                              "value": "Rourkela"
                            },
                            {
                              "name": "Rudrapur",
                              "value": "Rudrapur"
                            },
                            {
                              "name": "Ruteng",
                              "value": "Ruteng"
                            },
                            {
                              "name": "Sa Kaeo",
                              "value": "Sa Kaeo"
                            },
                            {
                              "name": "Sadiqabad",
                              "value": "Sadiqabad"
                            },
                            {
                              "name": "Sagar",
                              "value": "Sagar"
                            },
                            {
                              "name": "Saharanpur",
                              "value": "Saharanpur"
                            },
                            {
                              "name": "Saharsa",
                              "value": "Saharsa"
                            },
                            {
                              "name": "Sahiwal",
                              "value": "Sahiwal"
                            },
                            {
                              "name": "Saidu",
                              "value": "Saidu"
                            },
                            {
                              "name": "Sakhon Nakhon",
                              "value": "Sakhon Nakhon"
                            },
                            {
                              "name": "Salatiga",
                              "value": "Salatiga"
                            },
                            {
                              "name": "Sale",
                              "value": "Sale"
                            },
                            {
                              "name": "Salem",
                              "value": "Salem"
                            },
                            {
                              "name": "Samarinda",
                              "value": "Samarinda"
                            },
                            {
                              "name": "Sambalpur",
                              "value": "Sambalpur"
                            },
                            {
                              "name": "Sambhal",
                              "value": "Sambhal"
                            },
                            {
                              "name": "Sampit",
                              "value": "Sampit"
                            },
                            {
                              "name": "Samut Prakan",
                              "value": "Samut Prakan"
                            },
                            {
                              "name": "Samut Sakhon",
                              "value": "Samut Sakhon"
                            },
                            {
                              "name": "Samut Songkhram",
                              "value": "Samut Songkhram"
                            },
                            {
                              "name": "Sangli-Miraj & Kupwad",
                              "value": "Sangli-Miraj & Kupwad"
                            },
                            {
                              "name": "Santipur",
                              "value": "Santipur"
                            },
                            {
                              "name": "Saraburi",
                              "value": "Saraburi"
                            },
                            {
                              "name": "Sargodha",
                              "value": "Sargodha"
                            },
                            {
                              "name": "Sasaram",
                              "value": "Sasaram"
                            },
                            {
                              "name": "Satara",
                              "value": "Satara"
                            },
                            {
                              "name": "Satna",
                              "value": "Satna"
                            },
                            {
                              "name": "Satun",
                              "value": "Satun"
                            },
                            {
                              "name": "Sawahlunto",
                              "value": "Sawahlunto"
                            },
                            {
                              "name": "Sawai Madhopur",
                              "value": "Sawai Madhopur"
                            },
                            {
                              "name": "Scone",
                              "value": "Scone"
                            },
                            {
                              "name": "Sea Lake",
                              "value": "Sea Lake"
                            },
                            {
                              "name": "Secunderabad",
                              "value": "Secunderabad"
                            },
                            {
                              "name": "Sehore",
                              "value": "Sehore"
                            },
                            {
                              "name": "Semarang",
                              "value": "Semarang"
                            },
                            {
                              "name": "Seoni",
                              "value": "Seoni"
                            },
                            {
                              "name": "Serampore",
                              "value": "Serampore"
                            },
                            {
                              "name": "Seymour",
                              "value": "Seymour"
                            },
                            {
                              "name": "Shaheed Bhagat Singh Nagar ",
                              "value": "Shaheed Bhagat Singh Nagar "
                            },
                            {
                              "name": "Shahjahanpur",
                              "value": "Shahjahanpur"
                            },
                            {
                              "name": "Shamli",
                              "value": "Shamli"
                            },
                            {
                              "name": "Sheikhu Pura",
                              "value": "Sheikhu Pura"
                            },
                            {
                              "name": "Shepparton",
                              "value": "Shepparton"
                            },
                            {
                              "name": "Shikohabad",
                              "value": "Shikohabad"
                            },
                            {
                              "name": "Shillong",
                              "value": "Shillong"
                            },
                            {
                              "name": "Shimla",
                              "value": "Shimla"
                            },
                            {
                              "name": "Shirdi",
                              "value": "Shirdi"
                            },
                            {
                              "name": "Shivamogga",
                              "value": "Shivamogga"
                            },
                            {
                              "name": "Shivpuri",
                              "value": "Shivpuri"
                            },
                            {
                              "name": "Si Racha",
                              "value": "Si Racha"
                            },
                            {
                              "name": "Sialkote",
                              "value": "Sialkote"
                            },
                            {
                              "name": "Sibolga",
                              "value": "Sibolga"
                            },
                            {
                              "name": "Sikar",
                              "value": "Sikar"
                            },
                            {
                              "name": "Silapathar",
                              "value": "Silapathar"
                            },
                            {
                              "name": "Silchar",
                              "value": "Silchar"
                            },
                            {
                              "name": "Siliguri",
                              "value": "Siliguri"
                            },
                            {
                              "name": "Sing Buri",
                              "value": "Sing Buri"
                            },
                            {
                              "name": "Singaraja",
                              "value": "Singaraja"
                            },
                            {
                              "name": "Singkawang",
                              "value": "Singkawang"
                            },
                            {
                              "name": "Singleton",
                              "value": "Singleton"
                            },
                            {
                              "name": "Singrauli",
                              "value": "Singrauli"
                            },
                            {
                              "name": "Sirsa",
                              "value": "Sirsa"
                            },
                            {
                              "name": "Sisaket",
                              "value": "Sisaket"
                            },
                            {
                              "name": "Sitamarhi",
                              "value": "Sitamarhi"
                            },
                            {
                              "name": "Sivasagar",
                              "value": "Sivasagar"
                            },
                            {
                              "name": "Siwan",
                              "value": "Siwan"
                            },
                            {
                              "name": "Smithton",
                              "value": "Smithton"
                            },
                            {
                              "name": "Solapur",
                              "value": "Solapur"
                            },
                            {
                              "name": "Sonari",
                              "value": "Sonari"
                            },
                            {
                              "name": "Songkhla",
                              "value": "Songkhla"
                            },
                            {
                              "name": "Sonipat",
                              "value": "Sonipat"
                            },
                            {
                              "name": "Soro",
                              "value": "Soro"
                            },
                            {
                              "name": "Sorong",
                              "value": "Sorong"
                            },
                            {
                              "name": "South 24 Parganas",
                              "value": "South 24 Parganas"
                            },
                            {
                              "name": "South Dumdum",
                              "value": "South Dumdum"
                            },
                            {
                              "name": "Sri Ganganagar",
                              "value": "Sri Ganganagar"
                            },
                            {
                              "name": "Sri Jawewardenepura Kotte",
                              "value": "Sri Jawewardenepura Kotte"
                            },
                            {
                              "name": "Srikakulam",
                              "value": "Srikakulam"
                            },
                            {
                              "name": "Srinagar",
                              "value": "Srinagar"
                            },
                            {
                              "name": "Stanley",
                              "value": "Stanley"
                            },
                            {
                              "name": "Stanthorpe",
                              "value": "Stanthorpe"
                            },
                            {
                              "name": "Sujangarh",
                              "value": "Sujangarh"
                            },
                            {
                              "name": "Sukabumi",
                              "value": "Sukabumi"
                            },
                            {
                              "name": "Sukhothai",
                              "value": "Sukhothai"
                            },
                            {
                              "name": "Sukkur",
                              "value": "Sukkur"
                            },
                            {
                              "name": "Sultanpur",
                              "value": "Sultanpur"
                            },
                            {
                              "name": "Sumenep",
                              "value": "Sumenep"
                            },
                            {
                              "name": "Sunbury",
                              "value": "Sunbury"
                            },
                            {
                              "name": "Sungaipenuh",
                              "value": "Sungaipenuh"
                            },
                            {
                              "name": "Supham Buri",
                              "value": "Supham Buri"
                            },
                            {
                              "name": "Surabaya",
                              "value": "Surabaya"
                            },
                            {
                              "name": "Surakarta",
                              "value": "Surakarta"
                            },
                            {
                              "name": "Surat",
                              "value": "Surat"
                            },
                            {
                              "name": "Surat Thani",
                              "value": "Surat Thani"
                            },
                            {
                              "name": "Surendranagar Dudhrej",
                              "value": "Surendranagar Dudhrej"
                            },
                            {
                              "name": "Surin",
                              "value": "Surin"
                            },
                            {
                              "name": "Suryapet",
                              "value": "Suryapet"
                            },
                            {
                              "name": "Svendborg",
                              "value": "Svendborg"
                            },
                            {
                              "name": "Swan Hill",
                              "value": "Swan Hill"
                            },
                            {
                              "name": "Sydney",
                              "value": "Sydney"
                            },
                            {
                              "name": "Tadepalligudem",
                              "value": "Tadepalligudem"
                            },
                            {
                              "name": "Tadipatri",
                              "value": "Tadipatri"
                            },
                            {
                              "name": "Tadpatri",
                              "value": "Tadpatri"
                            },
                            {
                              "name": "Tak",
                              "value": "Tak"
                            },
                            {
                              "name": "Tamworth",
                              "value": "Tamworth"
                            },
                            {
                              "name": "Tanjungpandan",
                              "value": "Tanjungpandan"
                            },
                            {
                              "name": "Tarakan",
                              "value": "Tarakan"
                            },
                            {
                              "name": "Taree",
                              "value": "Taree"
                            },
                            {
                              "name": "Tarutung",
                              "value": "Tarutung"
                            },
                            {
                              "name": "Tasikmalaya",
                              "value": "Tasikmalaya"
                            },
                            {
                              "name": "Tebingtinggi",
                              "value": "Tebingtinggi"
                            },
                            {
                              "name": "Tegal",
                              "value": "Tegal"
                            },
                            {
                              "name": "Telukbutun",
                              "value": "Telukbutun"
                            },
                            {
                              "name": "Temora",
                              "value": "Temora"
                            },
                            {
                              "name": "Tenali",
                              "value": "Tenali"
                            },
                            {
                              "name": "Tennant Creek",
                              "value": "Tennant Creek"
                            },
                            {
                              "name": "Tenterfield",
                              "value": "Tenterfield"
                            },
                            {
                              "name": "Ternate",
                              "value": "Ternate"
                            },
                            {
                              "name": "Tezpur",
                              "value": "Tezpur"
                            },
                            {
                              "name": "Thane",
                              "value": "Thane"
                            },
                            {
                              "name": "Thanjavur",
                              "value": "Thanjavur"
                            },
                            {
                              "name": "Thiruvallur",
                              "value": "Thiruvallur"
                            },
                            {
                              "name": "Thiruvananthapuram",
                              "value": "Thiruvananthapuram"
                            },
                            {
                              "name": "Thoothukudi",
                              "value": "Thoothukudi"
                            },
                            {
                              "name": "Thrissur",
                              "value": "Thrissur"
                            },
                            {
                              "name": "Thung Song",
                              "value": "Thung Song"
                            },
                            {
                              "name": "Timika",
                              "value": "Timika"
                            },
                            {
                              "name": "Tinsukia",
                              "value": "Tinsukia"
                            },
                            {
                              "name": "Tiruchirappalli",
                              "value": "Tiruchirappalli"
                            },
                            {
                              "name": "Tirunelveli",
                              "value": "Tirunelveli"
                            },
                            {
                              "name": "Tirupati",
                              "value": "Tirupati"
                            },
                            {
                              "name": "Tiruppur",
                              "value": "Tiruppur"
                            },
                            {
                              "name": "Tirupur",
                              "value": "Tirupur"
                            },
                            {
                              "name": "Tiruvannamalai",
                              "value": "Tiruvannamalai"
                            },
                            {
                              "name": "Tiruvottiyur",
                              "value": "Tiruvottiyur"
                            },
                            {
                              "name": "Titagarh",
                              "value": "Titagarh"
                            },
                            {
                              "name": "Tom Price",
                              "value": "Tom Price"
                            },
                            {
                              "name": "Toowoomba",
                              "value": "Toowoomba"
                            },
                            {
                              "name": "Townsville",
                              "value": "Townsville"
                            },
                            {
                              "name": "Trang",
                              "value": "Trang"
                            },
                            {
                              "name": "Traralgon",
                              "value": "Traralgon"
                            },
                            {
                              "name": "Trat",
                              "value": "Trat"
                            },
                            {
                              "name": "Trincomalee",
                              "value": "Trincomalee"
                            },
                            {
                              "name": "Tuban",
                              "value": "Tuban"
                            },
                            {
                              "name": "Tumkur",
                              "value": "Tumkur"
                            },
                            {
                              "name": "Tumsar",
                              "value": "Tumsar"
                            },
                            {
                              "name": "Tumut",
                              "value": "Tumut"
                            },
                            {
                              "name": "Turbat",
                              "value": "Turbat"
                            },
                            {
                              "name": "Ubon Ratchathani",
                              "value": "Ubon Ratchathani"
                            },
                            {
                              "name": "Udaipur",
                              "value": "Udaipur"
                            },
                            {
                              "name": "Udgir",
                              "value": "Udgir"
                            },
                            {
                              "name": "Udon Thani",
                              "value": "Udon Thani"
                            },
                            {
                              "name": "Udupi",
                              "value": "Udupi"
                            },
                            {
                              "name": "Ujjain",
                              "value": "Ujjain"
                            },
                            {
                              "name": "Ujungpandang",
                              "value": "Ujungpandang"
                            },
                            {
                              "name": "Ulhasnagar",
                              "value": "Ulhasnagar"
                            },
                            {
                              "name": "Ulladulla",
                              "value": "Ulladulla"
                            },
                            {
                              "name": "Uluberia",
                              "value": "Uluberia"
                            },
                            {
                              "name": "Ulverstone",
                              "value": "Ulverstone"
                            },
                            {
                              "name": "Unnao",
                              "value": "Unnao"
                            },
                            {
                              "name": "Uthai Thani",
                              "value": "Uthai Thani"
                            },
                            {
                              "name": "Uttaradit",
                              "value": "Uttaradit"
                            },
                            {
                              "name": "Vadodara",
                              "value": "Vadodara"
                            },
                            {
                              "name": "Valsad",
                              "value": "Valsad"
                            },
                            {
                              "name": "Varanasi",
                              "value": "Varanasi"
                            },
                            {
                              "name": "Vasai-Virar",
                              "value": "Vasai-Virar"
                            },
                            {
                              "name": "Vejle",
                              "value": "Vejle"
                            },
                            {
                              "name": "Vellore",
                              "value": "Vellore"
                            },
                            {
                              "name": "Veraval",
                              "value": "Veraval"
                            },
                            {
                              "name": "Viborg",
                              "value": "Viborg"
                            },
                            {
                              "name": "Victor Harbor",
                              "value": "Victor Harbor"
                            },
                            {
                              "name": "Vijayanagaram",
                              "value": "Vijayanagaram"
                            },
                            {
                              "name": "Vijayawada",
                              "value": "Vijayawada"
                            },
                            {
                              "name": "Visakhapatnam",
                              "value": "Visakhapatnam"
                            },
                            {
                              "name": "Wagga Wagga",
                              "value": "Wagga Wagga"
                            },
                            {
                              "name": "Waingapu",
                              "value": "Waingapu"
                            },
                            {
                              "name": "Wangaratta",
                              "value": "Wangaratta"
                            },
                            {
                              "name": "Warangal",
                              "value": "Warangal"
                            },
                            {
                              "name": "Wardha",
                              "value": "Wardha"
                            },
                            {
                              "name": "Warragul",
                              "value": "Warragul"
                            },
                            {
                              "name": "Warwick",
                              "value": "Warwick"
                            },
                            {
                              "name": "Watampone",
                              "value": "Watampone"
                            },
                            {
                              "name": "Wauchope",
                              "value": "Wauchope"
                            },
                            {
                              "name": "Weipa",
                              "value": "Weipa"
                            },
                            {
                              "name": "Wellington",
                              "value": "Wellington"
                            },
                            {
                              "name": "Werribee",
                              "value": "Werribee"
                            },
                            {
                              "name": "West Wyalong",
                              "value": "West Wyalong"
                            },
                            {
                              "name": "Whyalla",
                              "value": "Whyalla"
                            },
                            {
                              "name": "Windsor",
                              "value": "Windsor"
                            },
                            {
                              "name": "Winton",
                              "value": "Winton"
                            },
                            {
                              "name": "Wollongong",
                              "value": "Wollongong"
                            },
                            {
                              "name": "Wonthaggi",
                              "value": "Wonthaggi"
                            },
                            {
                              "name": "Wyndham",
                              "value": "Wyndham"
                            },
                            {
                              "name": "Wynyard",
                              "value": "Wynyard"
                            },
                            {
                              "name": "Wyong",
                              "value": "Wyong"
                            },
                            {
                              "name": "Yala",
                              "value": "Yala"
                            },
                            {
                              "name": "Yamunanagar",
                              "value": "Yamunanagar"
                            },
                            {
                              "name": "Yarrawonga",
                              "value": "Yarrawonga"
                            },
                            {
                              "name": "Yasothon",
                              "value": "Yasothon"
                            },
                            {
                              "name": "Yass",
                              "value": "Yass"
                            },
                            {
                              "name": "Yavatmal",
                              "value": "Yavatmal"
                            },
                            {
                              "name": "Yeppoon",
                              "value": "Yeppoon"
                            },
                            {
                              "name": "Young",
                              "value": "Young"
                            },
                            {
                              "name": "Zhob",
                              "value": "Zhob"
                            }
                          ],
                          "placeholder": "Select Center",
                          "type": "multi-select"
                        },
                        {
                          "name": "Age",
                          "key": "age",
                          "options": {
                            "from": {
                              "options": [
                                {
                                  "name": "18",
                                  "value": 18
                                },
                                {
                                  "name": "19",
                                  "value": 19
                                },
                                {
                                  "name": "20",
                                  "value": 20
                                },
                                {
                                  "name": "21",
                                  "value": 21
                                },
                                {
                                  "name": "22",
                                  "value": 22
                                },
                                {
                                  "name": "23",
                                  "value": 23
                                },
                                {
                                  "name": "24",
                                  "value": 24
                                },
                                {
                                  "name": "25",
                                  "value": 25
                                },
                                {
                                  "name": "26",
                                  "value": 26
                                },
                                {
                                  "name": "27",
                                  "value": 27
                                },
                                {
                                  "name": "28",
                                  "value": 28
                                },
                                {
                                  "name": "29",
                                  "value": 29
                                },
                                {
                                  "name": "30",
                                  "value": 30
                                },
                                {
                                  "name": "31",
                                  "value": 31
                                },
                                {
                                  "name": "32",
                                  "value": 32
                                },
                                {
                                  "name": "33",
                                  "value": 33
                                },
                                {
                                  "name": "34",
                                  "value": 34
                                },
                                {
                                  "name": "35",
                                  "value": 35
                                },
                                {
                                  "name": "36",
                                  "value": 36
                                },
                                {
                                  "name": "37",
                                  "value": 37
                                },
                                {
                                  "name": "38",
                                  "value": 38
                                },
                                {
                                  "name": "39",
                                  "value": 39
                                },
                                {
                                  "name": "40",
                                  "value": 40
                                },
                                {
                                  "name": "41",
                                  "value": 41
                                },
                                {
                                  "name": "42",
                                  "value": 42
                                },
                                {
                                  "name": "43",
                                  "value": 43
                                },
                                {
                                  "name": "44",
                                  "value": 44
                                },
                                {
                                  "name": "45",
                                  "value": 45
                                },
                                {
                                  "name": "46",
                                  "value": 46
                                },
                                {
                                  "name": "47",
                                  "value": 47
                                },
                                {
                                  "name": "48",
                                  "value": 48
                                },
                                {
                                  "name": "49",
                                  "value": 49
                                },
                                {
                                  "name": "50",
                                  "value": 50
                                },
                                {
                                  "name": "51",
                                  "value": 51
                                },
                                {
                                  "name": "52",
                                  "value": 52
                                },
                                {
                                  "name": "53",
                                  "value": 53
                                },
                                {
                                  "name": "54",
                                  "value": 54
                                },
                                {
                                  "name": "55",
                                  "value": 55
                                },
                                {
                                  "name": "56",
                                  "value": 56
                                },
                                {
                                  "name": "57",
                                  "value": 57
                                },
                                {
                                  "name": "58",
                                  "value": 58
                                },
                                {
                                  "name": "59",
                                  "value": 59
                                },
                                {
                                  "name": "60",
                                  "value": 60
                                },
                                {
                                  "name": "61",
                                  "value": 61
                                },
                                {
                                  "name": "62",
                                  "value": 62
                                },
                                {
                                  "name": "63",
                                  "value": 63
                                },
                                {
                                  "name": "64",
                                  "value": 64
                                },
                                {
                                  "name": "65",
                                  "value": 65
                                },
                                {
                                  "name": "66",
                                  "value": 66
                                },
                                {
                                  "name": "67",
                                  "value": 67
                                },
                                {
                                  "name": "68",
                                  "value": 68
                                },
                                {
                                  "name": "69",
                                  "value": 69
                                },
                                {
                                  "name": "70",
                                  "value": 70
                                },
                                {
                                  "name": "71",
                                  "value": 71
                                },
                                {
                                  "name": "72",
                                  "value": 72
                                },
                                {
                                  "name": "73",
                                  "value": 73
                                },
                                {
                                  "name": "74",
                                  "value": 74
                                },
                                {
                                  "name": "75",
                                  "value": 75
                                },
                                {
                                  "name": "76",
                                  "value": 76
                                },
                                {
                                  "name": "77",
                                  "value": 77
                                },
                                {
                                  "name": "78",
                                  "value": 78
                                },
                                {
                                  "name": "79",
                                  "value": 79
                                },
                                {
                                  "name": "80",
                                  "value": 80
                                },
                                {
                                  "name": "81",
                                  "value": 81
                                },
                                {
                                  "name": "82",
                                  "value": 82
                                },
                                {
                                  "name": "83",
                                  "value": 83
                                },
                                {
                                  "name": "84",
                                  "value": 84
                                },
                                {
                                  "name": "85",
                                  "value": 85
                                },
                                {
                                  "name": "86",
                                  "value": 86
                                },
                                {
                                  "name": "87",
                                  "value": 87
                                },
                                {
                                  "name": "88",
                                  "value": 88
                                },
                                {
                                  "name": "89",
                                  "value": 89
                                },
                                {
                                  "name": "90",
                                  "value": 90
                                },
                                {
                                  "name": "91",
                                  "value": 91
                                },
                                {
                                  "name": "92",
                                  "value": 92
                                },
                                {
                                  "name": "93",
                                  "value": 93
                                },
                                {
                                  "name": "94",
                                  "value": 94
                                },
                                {
                                  "name": "95",
                                  "value": 95
                                },
                                {
                                  "name": "96",
                                  "value": 96
                                },
                                {
                                  "name": "97",
                                  "value": 97
                                },
                                {
                                  "name": "98",
                                  "value": 98
                                },
                                {
                                  "name": "99",
                                  "value": 99
                                },
                                {
                                  "name": "100",
                                  "value": 100
                                },
                                {
                                  "name": "101",
                                  "value": 101
                                },
                                {
                                  "name": "102",
                                  "value": 102
                                },
                                {
                                  "name": "103",
                                  "value": 103
                                },
                                {
                                  "name": "104",
                                  "value": 104
                                },
                                {
                                  "name": "105",
                                  "value": 105
                                },
                                {
                                  "name": "106",
                                  "value": 106
                                },
                                {
                                  "name": "107",
                                  "value": 107
                                },
                                {
                                  "name": "108",
                                  "value": 108
                                },
                                {
                                  "name": "109",
                                  "value": 109
                                },
                                {
                                  "name": "110",
                                  "value": 110
                                },
                                {
                                  "name": "111",
                                  "value": 111
                                },
                                {
                                  "name": "112",
                                  "value": 112
                                },
                                {
                                  "name": "113",
                                  "value": 113
                                },
                                {
                                  "name": "114",
                                  "value": 114
                                },
                                {
                                  "name": "115",
                                  "value": 115
                                },
                                {
                                  "name": "116",
                                  "value": 116
                                }
                              ],
                              "from_value": "",
                              "placeholder": "From"
                            },
                            "to": {
                              "to_value": "",
                              "placeholder": "To",
                              "options": [
                                {
                                  "name": "18",
                                  "value": 18
                                },
                                {
                                  "name": "19",
                                  "value": 19
                                },
                                {
                                  "name": "20",
                                  "value": 20
                                },
                                {
                                  "name": "21",
                                  "value": 21
                                },
                                {
                                  "name": "22",
                                  "value": 22
                                },
                                {
                                  "name": "23",
                                  "value": 23
                                },
                                {
                                  "name": "24",
                                  "value": 24
                                },
                                {
                                  "name": "25",
                                  "value": 25
                                },
                                {
                                  "name": "26",
                                  "value": 26
                                },
                                {
                                  "name": "27",
                                  "value": 27
                                },
                                {
                                  "name": "28",
                                  "value": 28
                                },
                                {
                                  "name": "29",
                                  "value": 29
                                },
                                {
                                  "name": "30",
                                  "value": 30
                                },
                                {
                                  "name": "31",
                                  "value": 31
                                },
                                {
                                  "name": "32",
                                  "value": 32
                                },
                                {
                                  "name": "33",
                                  "value": 33
                                },
                                {
                                  "name": "34",
                                  "value": 34
                                },
                                {
                                  "name": "35",
                                  "value": 35
                                },
                                {
                                  "name": "36",
                                  "value": 36
                                },
                                {
                                  "name": "37",
                                  "value": 37
                                },
                                {
                                  "name": "38",
                                  "value": 38
                                },
                                {
                                  "name": "39",
                                  "value": 39
                                },
                                {
                                  "name": "40",
                                  "value": 40
                                },
                                {
                                  "name": "41",
                                  "value": 41
                                },
                                {
                                  "name": "42",
                                  "value": 42
                                },
                                {
                                  "name": "43",
                                  "value": 43
                                },
                                {
                                  "name": "44",
                                  "value": 44
                                },
                                {
                                  "name": "45",
                                  "value": 45
                                },
                                {
                                  "name": "46",
                                  "value": 46
                                },
                                {
                                  "name": "47",
                                  "value": 47
                                },
                                {
                                  "name": "48",
                                  "value": 48
                                },
                                {
                                  "name": "49",
                                  "value": 49
                                },
                                {
                                  "name": "50",
                                  "value": 50
                                },
                                {
                                  "name": "51",
                                  "value": 51
                                },
                                {
                                  "name": "52",
                                  "value": 52
                                },
                                {
                                  "name": "53",
                                  "value": 53
                                },
                                {
                                  "name": "54",
                                  "value": 54
                                },
                                {
                                  "name": "55",
                                  "value": 55
                                },
                                {
                                  "name": "56",
                                  "value": 56
                                },
                                {
                                  "name": "57",
                                  "value": 57
                                },
                                {
                                  "name": "58",
                                  "value": 58
                                },
                                {
                                  "name": "59",
                                  "value": 59
                                },
                                {
                                  "name": "60",
                                  "value": 60
                                },
                                {
                                  "name": "61",
                                  "value": 61
                                },
                                {
                                  "name": "62",
                                  "value": 62
                                },
                                {
                                  "name": "63",
                                  "value": 63
                                },
                                {
                                  "name": "64",
                                  "value": 64
                                },
                                {
                                  "name": "65",
                                  "value": 65
                                },
                                {
                                  "name": "66",
                                  "value": 66
                                },
                                {
                                  "name": "67",
                                  "value": 67
                                },
                                {
                                  "name": "68",
                                  "value": 68
                                },
                                {
                                  "name": "69",
                                  "value": 69
                                },
                                {
                                  "name": "70",
                                  "value": 70
                                },
                                {
                                  "name": "71",
                                  "value": 71
                                },
                                {
                                  "name": "72",
                                  "value": 72
                                },
                                {
                                  "name": "73",
                                  "value": 73
                                },
                                {
                                  "name": "74",
                                  "value": 74
                                },
                                {
                                  "name": "75",
                                  "value": 75
                                },
                                {
                                  "name": "76",
                                  "value": 76
                                },
                                {
                                  "name": "77",
                                  "value": 77
                                },
                                {
                                  "name": "78",
                                  "value": 78
                                },
                                {
                                  "name": "79",
                                  "value": 79
                                },
                                {
                                  "name": "80",
                                  "value": 80
                                },
                                {
                                  "name": "81",
                                  "value": 81
                                },
                                {
                                  "name": "82",
                                  "value": 82
                                },
                                {
                                  "name": "83",
                                  "value": 83
                                },
                                {
                                  "name": "84",
                                  "value": 84
                                },
                                {
                                  "name": "85",
                                  "value": 85
                                },
                                {
                                  "name": "86",
                                  "value": 86
                                },
                                {
                                  "name": "87",
                                  "value": 87
                                },
                                {
                                  "name": "88",
                                  "value": 88
                                },
                                {
                                  "name": "89",
                                  "value": 89
                                },
                                {
                                  "name": "90",
                                  "value": 90
                                },
                                {
                                  "name": "91",
                                  "value": 91
                                },
                                {
                                  "name": "92",
                                  "value": 92
                                },
                                {
                                  "name": "93",
                                  "value": 93
                                },
                                {
                                  "name": "94",
                                  "value": 94
                                },
                                {
                                  "name": "95",
                                  "value": 95
                                },
                                {
                                  "name": "96",
                                  "value": 96
                                },
                                {
                                  "name": "97",
                                  "value": 97
                                },
                                {
                                  "name": "98",
                                  "value": 98
                                },
                                {
                                  "name": "99",
                                  "value": 99
                                },
                                {
                                  "name": "100",
                                  "value": 100
                                },
                                {
                                  "name": "101",
                                  "value": 101
                                },
                                {
                                  "name": "102",
                                  "value": 102
                                },
                                {
                                  "name": "103",
                                  "value": 103
                                },
                                {
                                  "name": "104",
                                  "value": 104
                                },
                                {
                                  "name": "105",
                                  "value": 105
                                },
                                {
                                  "name": "106",
                                  "value": 106
                                },
                                {
                                  "name": "107",
                                  "value": 107
                                },
                                {
                                  "name": "108",
                                  "value": 108
                                },
                                {
                                  "name": "109",
                                  "value": 109
                                },
                                {
                                  "name": "110",
                                  "value": 110
                                },
                                {
                                  "name": "111",
                                  "value": 111
                                },
                                {
                                  "name": "112",
                                  "value": 112
                                },
                                {
                                  "name": "113",
                                  "value": 113
                                },
                                {
                                  "name": "114",
                                  "value": 114
                                },
                                {
                                  "name": "115",
                                  "value": 115
                                },
                                {
                                  "name": "116",
                                  "value": 116
                                }
                              ]
                            }
                          },
                          "type": "from-to",
                          "validation": {
                            "rule": "rangeValidation",
                            "message": "To value should be greater than or equal to from value",
                            "condition": "toValue >= fromValue"
                          }
                        },
                        {
                          "name": "NCCS",
                          "key": "nccs",
                          "options": [
                            {
                              "name": "A1",
                              "value": "A1"
                            },
                            {
                              "name": "A2",
                              "value": "A2"
                            },
                            {
                              "name": "A3",
                              "value": "A3"
                            },
                            {
                              "name": "B1",
                              "value": "B1"
                            },
                            {
                              "name": "B2",
                              "value": "B2"
                            }
                          ],
                          "placeholder": "Select NCCS",
                          "type": "multi-select"
                        }
                      ]
                    }
                  ]
                }       
          
    return this.advanceFilter = this.normalizeFilters(result.groups || []);
  }
  getAllFileAdvanceFilter() {
    this.getAdvanceFilter({}).subscribe((res:any) => {
      if(res && res.status == 1){
        this.advanceFilter = this.normalizeFilters(res.groups || []);
      }
    });
  }

  getDependentFilters(body: any) {
    // console.log("Single select changed for body: ", body);
    if (body && body.dependent && body.dependent.length > 0) {
      
      this.getAdvanceFilter(body).subscribe((res:any) => {
        if(res && res.status == 1){
          // this.resetDependentFilters(res.groups[0].filters);
          // this.resetDependentFilters(res.filter);
          res.groups.forEach(group => {
            if (group.filters && Array.isArray(group.filters)) {
              this.resetDependentFilters(group,body.dependent);
            }
          });
        }
      });
        // if (result && result.status === 1) {
        // }
    }
  }

  resetDependentFilters(group: any, dependent: any[]) {
    // console.log("Resetting dependent filters with:", dependent);
    if (!dependent || dependent.length === 0) return;

    // Create a map of updated filters from the API response group for efficient lookup
    const updatedFiltersMap = new Map();
    if (group.filters && Array.isArray(group.filters)) {
      group.filters.forEach(filter => {
        updatedFiltersMap.set(filter.key, filter);
      });
    }

    // Iterate through all groups in advanceFilter
    this.advanceFilter.forEach(advanceFilterGroup => {
      if (advanceFilterGroup.filters && Array.isArray(advanceFilterGroup.filters)) {
        // Only update filters that are in the dependent array
        advanceFilterGroup.filters.forEach(filter => {
          if (dependent.includes(filter.key)) {
            const updatedFilter = updatedFiltersMap.get(filter.key);

            if (updatedFilter && updatedFilter.options) {
              // Update the options with new options from API response
              filter.options = updatedFilter.options;
              // console.log("Updated filter options for key:", filter.key, updatedFilter.options);
              // Get list of valid values from updated options
              const validValues = updatedFilter.options.map(opt => opt.value);

              // Remove selected values that are not present in the new options
              if (filter.selectedValue !== undefined && filter.selectedValue !== null) {
                if (Array.isArray(filter.selectedValue)) {
                  // Multi-select case - filter out invalid values
                  filter.selectedValue = filter.selectedValue.filter(
                    val => validValues.includes(val)
                  );
                } else {
                  // Single select case - clear if not valid
                  if (!validValues.includes(filter.selectedValue)) {
                    filter.selectedValue = null;
                  }
                }
              }
            }
          }
        });
      }
    });
  }

  // resetDependentFilters(filters: any[]) {
  //   console.log("Resetting dependent filters with:", filters);
  //   if (!filters || filters.length === 0) return;

  //   // Iterate through all groups in advanceFilter
  //   this.advanceFilter.forEach(group => {
  //     if (group.filters && Array.isArray(group.filters)) {
  //       // Iterate through all filters within the group
  //       group.filters.forEach(filter => {
  //         // Find the matching filter in updated filters by key
  //         const updatedFilter = filters.find(f => f.key === filter.key);

  //         if (updatedFilter && updatedFilter.options) {
  //           // Update the options with new options
  //           filter.options = updatedFilter.options;
  //           console.log("Updated filter options for key:", filter.key, updatedFilter.options);
  //           // Get list of valid values from updated options
  //           const validValues = updatedFilter.options.map(opt => opt.value);

  //           // Remove selected values that are not present in the new options
  //           if (filter.selectedValue !== undefined && filter.selectedValue !== null) {
  //             if (Array.isArray(filter.selectedValue)) {
  //               // Multi-select case - filter out invalid values
  //               filter.selectedValue = filter.selectedValue.filter(
  //                 val => validValues.includes(val)
  //               );
  //             } else {
  //               // Single select case - clear if not valid
  //               if (!validValues.includes(filter.selectedValue)) {
  //                 filter.selectedValue = null;
  //               }
  //             }
  //           }
  //         }
  //       });
  //     }
  //   });
  // }

  getAdvanceFilter(body: any = {}){
    let pathUrl = '/v2/repo/getfilteroptions';
    let opt = {};
    return this.apiRequest.request('POST',pathUrl,body,opt);
  }
  
  // getAdvanceFilter(body: any = {}){
  //   let pathUrl = '/v2/repo/getfilteroptions';
  //   let opt = {};
  //   return this.apiRequest.request('GET',pathUrl,{},opt);
  // }
}
